var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, Property, Event, Animation, Collection, append } from '@syncfusion/ej2-base';
import { EventHandler, Browser, Internationalization, getDefaultDateObject, cldrData, L10n } from '@syncfusion/ej2-base';
import { getValue, compile, extend, isNullOrUndefined, NotifyPropertyChanges, Complex } from '@syncfusion/ej2-base';
import { getElement, removeClass, addClass, classList, remove, SanitizeHtmlHelper } from '@syncfusion/ej2-base';
import { createSpinner, hideSpinner, showSpinner } from '@syncfusion/ej2-popups';
import { HeaderRenderer } from '../renderer/header-renderer';
import { Scroll } from '../actions/scroll';
import { ScheduleTouch } from '../actions/touch';
import { KeyboardInteraction } from '../actions/keyboard';
import { Data } from '../actions/data';
import { EventBase } from '../event-renderer/event-base';
import { InlineEdit } from '../event-renderer/inline-edit';
import { QuickPopups } from '../popups/quick-popups';
import { EventTooltip } from '../popups/event-tooltip';
import { EventWindow } from '../popups/event-window';
import { Render } from '../renderer/renderer';
import { WorkHours } from '../models/work-hours';
import { TimeScale } from '../models/time-scale';
import { QuickInfoTemplates } from '../models/quick-info-templates';
import { HeaderRows } from '../models/header-rows';
import { Crud } from '../actions/crud';
import { WorkCellInteraction } from '../actions/work-cells';
import { EventSettings } from '../models/event-settings';
import { Group } from '../models/group';
import { Resources } from '../models/resources';
import { Gregorian, Islamic } from '../../common/calendar-util';
import { ResourceBase } from '../base/resource';
import { Timezone, timezoneData } from '../timezone/timezone';
import * as events from '../base/constant';
import * as cls from '../base/css-constant';
import * as util from '../base/util';
import { ToolbarItem } from '../models/toolbar';
/**
 * Represents the Schedule component that displays a list of events scheduled against specific date and timings,
 * thus helping us to plan and manage it properly.
 * ```html
 * <div id="schedule"></div>
 * ```
 * ```typescript
 * <script>
 *   var scheduleObj = new Schedule();
 *   scheduleObj.appendTo("#schedule");
 * </script>
 * ```
 */
var Schedule = /** @class */ (function (_super) {
    __extends(Schedule, _super);
    /**
     * Constructor for creating the Schedule widget
     *
     * @param {ScheduleModel} options Accepts the schedule model properties to initiate the rendering
     * @param {string | HTMLElement} element Accepts the DOM element reference
     */
    function Schedule(options, element) {
        var _this = _super.call(this, options, element) || this;
        _this.adaptiveGroupIndex = 0;
        return _this;
    }
    /**
     * Core method that initializes the control rendering.
     *
     * @returns {void}
     * @private
     */
    Schedule.prototype.render = function () {
        var addClasses = [];
        var removeClasses = [];
        addClasses.push(cls.ROOT);
        if (this.enableRtl) {
            addClasses.push(cls.RTL);
        }
        else {
            removeClasses.push(cls.RTL);
        }
        if (this.isAdaptive) {
            addClasses.push(cls.DEVICE_CLASS);
        }
        else {
            removeClasses.push(cls.DEVICE_CLASS);
        }
        if (this.enableAdaptiveUI) {
            addClasses.push(cls.ADAPTIVE_CLASS);
        }
        else {
            removeClasses.push(cls.ADAPTIVE_CLASS);
        }
        if (this.allowMultiDrag) {
            addClasses.push(cls.MULTI_DRAG);
        }
        else {
            removeClasses.push(cls.MULTI_DRAG);
        }
        if (this.cssClass) {
            var cssClass = this.cssClass.split(' ');
            for (var _i = 0, cssClass_1 = cssClass; _i < cssClass_1.length; _i++) {
                var cls_1 = cssClass_1[_i];
                addClasses.push(cls_1);
            }
        }
        classList(this.element, addClasses, removeClasses);
        this.validateDate();
        createSpinner({ target: this.element });
        this.scrollModule = new Scroll(this);
        this.scrollModule.setWidth();
        this.scrollModule.setHeight();
        this.renderModule = new Render(this);
        this.eventBase = new EventBase(this);
        this.workCellAction = new WorkCellInteraction(this);
        if (this.allowKeyboardInteraction) {
            this.keyboardInteractionModule = new KeyboardInteraction(this);
        }
        this.inlineModule = new InlineEdit(this);
        this.initializeDataModule();
        this.renderTableContainer();
        this.activeViewOptions = this.getActiveViewOptions();
        this.initializeResources();
        this.wireEvents();
    };
    Schedule.prototype.renderTableContainer = function () {
        if (!this.element.querySelector('.' + cls.TABLE_CONTAINER_CLASS)) {
            this.element.appendChild(this.createElement('div', { className: cls.TABLE_CONTAINER_CLASS }));
        }
    };
    Schedule.prototype.getEventTemplateName = function (resIndex) {
        var templateName = 'eventTemplate_' + resIndex;
        if (this.activeEventTemplates.indexOf(templateName) < 0) {
            this.activeEventTemplates.push(templateName);
        }
        return templateName;
    };
    /**
     * Method to get element width
     *
     * @param {HTMLElement} element Accepts the DOM element
     * @returns {number} Returns the width of the given element
     * @private
     */
    Schedule.prototype.getElementWidth = function (element) {
        return util.getElementWidth(element, this.uiStateValues.isTransformed);
    };
    /**
     * Method to get element height
     *
     * @param {HTMLElement} element Accepts the DOM element
     * @returns {number} Returns the Height of the given element
     * @private
     */
    Schedule.prototype.getElementHeight = function (element) {
        return util.getElementHeight(element, this.uiStateValues.isTransformed);
    };
    /**
     * Method to get height from element
     *
     * @param {Element} element Accepts the DOM element
     * @param {string} elementClass Accepts the element class
     * @returns {number} Returns the height of the element
     * @private
     */
    Schedule.prototype.getElementHeightFromClass = function (element, elementClass) {
        return util.getElementHeightFromClass(element, elementClass, this.uiStateValues.isTransformed);
    };
    /**
     * Method to render react templates
     *
     * @param {Function} callback - Specifies the callBack method
     * @returns {void}
     * @private
     */
    Schedule.prototype.renderTemplates = function (callback) {
        if (this.isReact) {
            this.renderReactTemplates(callback);
        }
        else if (callback) {
            callback();
        }
    };
    /**
     * Method to reset react templates
     *
     * @param {string[]} templates Accepts the template ID
     * @returns {void}
     * @private
     */
    Schedule.prototype.resetTemplates = function (templates) {
        if (this.isAngular || this.isReact) {
            this.clearTemplate(templates);
        }
    };
    /**
     * This method renders untrusted strings and scripts securely by sanitizing them first.
     *
     * @param {string} value - A string value representing the HTML string value to be sanitized.
     * @param {HTMLElement} element - An HTML element to which the sanitized or unsanitized HTML string will be assigned.
     * @returns {void}
     * @private
     */
    Schedule.prototype.sanitize = function (value, element) {
        if (this.enableHtmlSanitizer) {
            element.innerText = SanitizeHtmlHelper.sanitize(value);
        }
        else {
            element.innerHTML = value;
        }
    };
    Schedule.prototype.initializeResources = function (isSetModel) {
        if (isSetModel === void 0) { isSetModel = false; }
        if (this.resources.length > 0) {
            this.resourceBase = new ResourceBase(this);
            this.resourceBase.bindResourcesData(isSetModel);
        }
        else {
            this.resourceBase = null;
            this.resourceCollection = [];
            this.renderElements(isSetModel);
        }
    };
    Schedule.prototype.destroyEditorWindow = function () {
        if (this.eventWindow) {
            this.eventWindow.destroy();
            this.eventWindow = null;
        }
        this.eventWindow = new EventWindow(this);
    };
    /**
     * Method to render the layout elements
     *
     * @param {boolean} isLayoutOnly Accepts the boolean value to render layout or not
     * @returns {void}
     * @private
     */
    Schedule.prototype.renderElements = function (isLayoutOnly) {
        if (isLayoutOnly) {
            this.initializeView(this.currentView);
            this.eventWindow.refresh();
            return;
        }
        this.destroyHeaderModule();
        if (this.showHeaderBar) {
            this.headerModule = new HeaderRenderer(this);
        }
        this.renderTableContainer();
        this.uiStateValues.isTransformed = Math.round(this.element.getBoundingClientRect().width) !== this.element.offsetWidth;
        if (Browser.isDevice || Browser.isTouch) {
            this.scheduleTouchModule = new ScheduleTouch(this);
        }
        this.initializeView(this.currentView);
        this.destroyPopups();
        if (!this.isPrinting) {
            this.initializePopups();
        }
    };
    Schedule.prototype.validateDate = function (selectedDate) {
        if (selectedDate === void 0) { selectedDate = this.selectedDate; }
        // persist the selected date value
        var date = selectedDate instanceof Date ? new Date(selectedDate.getTime()) : new Date(selectedDate);
        var minDate = isNullOrUndefined(this.minDate) ? new Date(1900, 0, 1) :
            this.minDate instanceof Date ? new Date(this.minDate.getTime()) : new Date(this.minDate);
        var maxDate = isNullOrUndefined(this.maxDate) ? new Date(2099, 11, 31) :
            this.maxDate instanceof Date ? new Date(this.maxDate.getTime()) : new Date(this.maxDate);
        if (minDate <= maxDate) {
            if (date < minDate) {
                date = minDate;
            }
            if (date > maxDate) {
                date = maxDate;
            }
            this.setProperties({ selectedDate: new Date('' + date), minDate: new Date('' + minDate), maxDate: new Date('' + maxDate) }, true);
            if (this.eventWindow) {
                this.eventWindow.updateMinMaxDateToEditor();
            }
        }
        else {
            throw Error('minDate should be equal or less than maxDate');
        }
    };
    Schedule.prototype.getViewIndex = function (viewName) {
        for (var item = 0; item < this.viewCollections.length; item++) {
            var checkIndex = this.viewCollections[parseInt(item.toString(), 10)].option;
            if (checkIndex === viewName) {
                return item;
            }
        }
        return -1;
    };
    Schedule.prototype.setViewOptions = function (isModuleLoad) {
        if (isModuleLoad === void 0) { isModuleLoad = false; }
        if (isNullOrUndefined(this.views) || this.views.length === 0) {
            return;
        }
        this.viewOptions = {};
        this.viewCollections = [];
        var viewName;
        var selectedView;
        var prevIndex = this.viewIndex;
        var count = 0;
        this.viewIndex = -1;
        for (var _i = 0, _a = this.views; _i < _a.length; _i++) {
            var view = _a[_i];
            var isOptions = (typeof view === 'string') ? false : true;
            if (typeof view === 'string') {
                viewName = view;
                if (this.currentView === viewName) {
                    selectedView = viewName;
                    this.viewIndex = count;
                }
            }
            else {
                viewName = view.option;
                if (view.isSelected) {
                    selectedView = viewName;
                    this.viewIndex = count;
                }
            }
            var obj = extend({ option: viewName }, isOptions ? view : {});
            var fieldViewName = viewName.charAt(0).toLowerCase() + viewName.slice(1);
            obj.cellHeaderTemplateName = obj.cellHeaderTemplate ? obj.option : '';
            obj.dateHeaderTemplateName = obj.dateHeaderTemplate ? obj.option : '';
            obj.dateRangeTemplateName = obj.dateRangeTemplate ? obj.option : '';
            obj.cellTemplateName = obj.cellTemplate ? obj.option : '';
            obj.dayHeaderTemplateName = obj.dayHeaderTemplate ? obj.option : '';
            obj.monthHeaderTemplateName = obj.monthHeaderTemplate ? obj.option : '';
            obj.resourceHeaderTemplateName = obj.resourceHeaderTemplate ? obj.option : '';
            obj.headerIndentTemplateName = obj.headerIndentTemplate ? obj.option : '';
            obj.eventTemplateName = obj.eventTemplate ? obj.option : '';
            if (!isNullOrUndefined(obj.firstDayOfWeek) && obj.firstDayOfWeek === 0) {
                delete obj.firstDayOfWeek;
            }
            if (!isNullOrUndefined(obj.interval) && obj.interval === 1) {
                delete obj.interval;
            }
            this.viewCollections.push(obj);
            if (isNullOrUndefined(this.viewOptions["" + fieldViewName])) {
                this.viewOptions["" + fieldViewName] = [obj];
            }
            else {
                this.viewOptions["" + fieldViewName].push(obj);
            }
            count++;
        }
        if (!isModuleLoad && selectedView) {
            this.setProperties({ currentView: selectedView }, true);
        }
        if (this.viewIndex === -1) {
            var currentIndex = this.getViewIndex(this.currentView);
            this.viewIndex = ((typeof this.views[0] !== 'string') && (!isNullOrUndefined(prevIndex) && prevIndex !== -1)) ? prevIndex :
                (currentIndex === -1) ? 0 : currentIndex;
        }
    };
    Schedule.prototype.getActiveViewOptions = function () {
        var timeScale = {
            enable: this.timeScale.enable,
            interval: this.timeScale.interval,
            slotCount: this.timeScale.slotCount,
            majorSlotTemplate: this.timeScale.majorSlotTemplate,
            minorSlotTemplate: this.timeScale.minorSlotTemplate
        };
        var isYearView = this.viewCollections[this.viewIndex].option.indexOf('Year') > -1;
        var group = {
            byDate: isYearView ? false : this.group.byDate,
            byGroupID: this.group.byGroupID,
            allowGroupEdit: this.group.allowGroupEdit,
            resources: isNullOrUndefined(this.group.resources) ? [] : this.group.resources,
            headerTooltipTemplate: this.group.headerTooltipTemplate,
            enableCompactView: this.group.enableCompactView,
            hideNonWorkingDays: ['Day', 'Week', 'WorkWeek', 'Month'].indexOf(this.currentView) > -1 ? this.group.hideNonWorkingDays : false
        };
        var workDays = this.viewCollections[this.viewIndex].workDays ? [] : this.workDays;
        var scheduleOptions = {
            dateFormat: this.dateFormat,
            endHour: this.endHour,
            isSelected: false,
            option: null,
            readonly: this.readonly,
            startHour: this.startHour,
            allowVirtualScrolling: false,
            cellHeaderTemplate: this.cellHeaderTemplate,
            dayHeaderTemplate: this.dayHeaderTemplate,
            monthHeaderTemplate: this.monthHeaderTemplate,
            cellTemplate: this.cellTemplate,
            eventTemplate: this.eventSettings.template,
            dateHeaderTemplate: this.dateHeaderTemplate,
            dateRangeTemplate: this.dateRangeTemplate,
            resourceHeaderTemplate: this.resourceHeaderTemplate,
            headerIndentTemplate: this.headerIndentTemplate,
            firstMonthOfYear: this.firstMonthOfYear,
            firstDayOfWeek: this.firstDayOfWeek,
            workDays: workDays,
            monthsCount: this.monthsCount,
            showWeekend: this.showWeekend,
            showWeekNumber: this.showWeekNumber,
            displayName: null,
            interval: 1,
            timeScale: timeScale,
            timeFormat: this.internalTimeFormat,
            group: group,
            headerRows: this.headerRows,
            orientation: 'Horizontal',
            numberOfWeeks: 0,
            displayDate: null,
            enableLazyLoading: false
        };
        var viewOptions = this.viewCollections[this.viewIndex];
        var viewsData = extend(scheduleOptions, viewOptions, undefined, true);
        if (this.firstDayOfWeek !== 0 && viewOptions.firstDayOfWeek && this.firstDayOfWeek !== viewOptions.firstDayOfWeek) {
            viewsData.firstDayOfWeek = this.firstDayOfWeek;
        }
        if (viewsData.displayDate) {
            viewsData.displayDate = viewsData.displayDate instanceof Date ? new Date(viewsData.displayDate.getTime()) :
                new Date(viewsData.displayDate);
        }
        if (viewsData.enableLazyLoading && !isNullOrUndefined(viewsData.group.resources) && viewsData.group.resources.length > 0 &&
            (['Agenda', 'MonthAgenda', 'Year', 'TimelineYear'].indexOf(viewsData.option) === -1 ||
                (viewsData.option === 'TimelineYear' && viewsData.orientation === 'Vertical'))) {
            viewsData.allowVirtualScrolling = true;
        }
        return viewsData;
    };
    Schedule.prototype.initializeDataModule = function () {
        this.eventFields = {
            id: this.eventSettings.fields.id,
            isBlock: this.eventSettings.fields.isBlock,
            subject: this.eventSettings.fields.subject.name,
            startTime: this.eventSettings.fields.startTime.name,
            endTime: this.eventSettings.fields.endTime.name,
            startTimezone: this.eventSettings.fields.startTimezone.name,
            endTimezone: this.eventSettings.fields.endTimezone.name,
            location: this.eventSettings.fields.location.name,
            description: this.eventSettings.fields.description.name,
            isAllDay: this.eventSettings.fields.isAllDay.name,
            recurrenceID: this.eventSettings.fields.recurrenceID.name,
            recurrenceRule: this.eventSettings.fields.recurrenceRule.name,
            recurrenceException: this.eventSettings.fields.recurrenceException.name,
            isReadonly: this.eventSettings.fields.isReadonly,
            followingID: this.eventSettings.fields.followingID
        };
        this.setEditorTitles();
        this.dataModule = new Data(this, this.eventSettings.dataSource, this.eventSettings.query);
        this.crudModule = new Crud(this);
    };
    Schedule.prototype.setEditorTitles = function () {
        this.editorTitles = {
            subject: this.eventSettings.fields.subject.title || this.localeObj.getConstant('title'),
            startTime: this.eventSettings.fields.startTime.title || this.localeObj.getConstant('start'),
            endTime: this.eventSettings.fields.endTime.title || this.localeObj.getConstant('end'),
            isAllDay: this.eventSettings.fields.isAllDay.title || this.localeObj.getConstant('allDay'),
            startTimezone: this.eventSettings.fields.startTimezone.title || this.localeObj.getConstant('startTimezone'),
            endTimezone: this.eventSettings.fields.endTimezone.title || this.localeObj.getConstant('endTimezone'),
            location: this.eventSettings.fields.location.title || this.localeObj.getConstant('location'),
            description: this.eventSettings.fields.description.title || this.localeObj.getConstant('description'),
            recurrenceRule: this.eventSettings.fields.recurrenceRule.title || this.localeObj.getConstant('repeat')
        };
    };
    Schedule.prototype.initializeView = function (viewName) {
        this.showSpinner();
        this.activeViewOptions = this.getActiveViewOptions();
        if (this.resourceBase) {
            this.resourceBase.setResourceCollection();
        }
        this.initializeTemplates();
        this.renderModule.render(viewName);
    };
    Schedule.prototype.initializeTemplates = function () {
        this.cellHeaderTemplateFn = this.templateParser(this.activeViewOptions.cellHeaderTemplate);
        this.dayHeaderTemplateFn = this.templateParser(this.activeViewOptions.dayHeaderTemplate);
        this.monthHeaderTemplateFn = this.templateParser(this.activeViewOptions.monthHeaderTemplate);
        this.cellTemplateFn = this.templateParser(this.activeViewOptions.cellTemplate);
        this.dateHeaderTemplateFn = this.templateParser(this.activeViewOptions.dateHeaderTemplate);
        this.dateRangeTemplateFn = this.templateParser(this.activeViewOptions.dateRangeTemplate);
        this.majorSlotTemplateFn = this.templateParser(this.activeViewOptions.timeScale.majorSlotTemplate);
        this.minorSlotTemplateFn = this.templateParser(this.activeViewOptions.timeScale.minorSlotTemplate);
        this.appointmentTemplateFn = this.templateParser(this.activeViewOptions.eventTemplate);
        this.resourceHeaderTemplateFn = this.templateParser(this.activeViewOptions.resourceHeaderTemplate);
        this.headerIndentTemplateFn = this.templateParser(this.activeViewOptions.headerIndentTemplate);
        this.headerTooltipTemplateFn = this.templateParser(this.activeViewOptions.group.headerTooltipTemplate);
        this.eventTooltipTemplateFn = this.templateParser(this.eventSettings.tooltipTemplate);
        this.editorTemplateFn = this.templateParser(this.editorTemplate);
        this.editorHeaderTemplateFn = this.templateParser(this.editorHeaderTemplate);
        this.editorFooterTemplateFn = this.templateParser(this.editorFooterTemplate);
        this.quickInfoTemplatesHeaderFn = this.templateParser(this.quickInfoTemplates.header);
        this.quickInfoTemplatesContentFn = this.templateParser(this.quickInfoTemplates.content);
        this.quickInfoTemplatesFooterFn = this.templateParser(this.quickInfoTemplates.footer);
    };
    Schedule.prototype.initializePopups = function () {
        this.eventWindow = new EventWindow(this);
        this.quickPopup = new QuickPopups(this);
    };
    /**
     * Method to get day names
     *
     * @param {string} type Accepts the day name
     * @returns {string[]} Returns the collection of day names
     * @private
     */
    Schedule.prototype.getDayNames = function (type) {
        var culShortNames = [];
        var cldrObj;
        var nameSpace = '';
        if (isNullOrUndefined(this.locale) || this.locale === 'en' || this.locale === 'en-US') {
            nameSpace = 'days.stand-alone.';
            cldrObj = (getValue(nameSpace + type, getDefaultDateObject(this.getCalendarMode())));
        }
        else {
            nameSpace = 'main.' + '' + this.locale + '.dates.calendars.' + this.getCalendarMode() + '.days.format.' + type;
            cldrObj = (getValue(nameSpace, cldrData));
        }
        for (var _i = 0, _a = Object.keys(cldrObj); _i < _a.length; _i++) {
            var obj = _a[_i];
            culShortNames.push(getValue(obj, cldrObj));
        }
        return culShortNames;
    };
    Schedule.prototype.setCldrTimeFormat = function () {
        if (!isNullOrUndefined(this.timeFormat)) {
            this.internalTimeFormat = this.timeFormat;
            return;
        }
        if (isNullOrUndefined(this.locale) || this.locale === 'en' || this.locale === 'en-US') {
            this.internalTimeFormat = (getValue('timeFormats.short', getDefaultDateObject(this.getCalendarMode())));
        }
        else {
            this.internalTimeFormat = (getValue('main.' + '' + this.locale + '.dates.calendars.' + this.getCalendarMode() + '.timeFormats.short', cldrData));
        }
    };
    /**
     * Method to get calendar mode
     *
     * @returns {string} Returns the calendar mode
     * @private
     */
    Schedule.prototype.getCalendarMode = function () {
        return !isNullOrUndefined(this.calendarMode) ? this.calendarMode.toLowerCase() : 'gregorian';
    };
    /**
     * Method to get time in string
     *
     * @param {Date} date Accepts the date object
     * @returns {string} Returns the time in string
     * @private
     */
    Schedule.prototype.getTimeString = function (date) {
        var time = this.globalize.formatDate(date, {
            format: this.activeViewOptions.timeFormat,
            type: 'time', calendar: this.getCalendarMode()
        });
        return time.toLocaleUpperCase();
    };
    /**
     * Method to get  date object
     *
     * @param {Date} date Accepts the date object
     * @returns {Date} Returns the date object
     * @private
     */
    Schedule.prototype.getDateTime = function (date) {
        return date instanceof Date ? new Date(date.getTime()) : new Date(date);
    };
    Schedule.prototype.setCalendarMode = function () {
        if (this.calendarMode === 'Islamic') {
            this.calendarUtil = new Islamic();
        }
        else {
            this.calendarUtil = new Gregorian();
        }
    };
    /**
     * Method to change the current view
     *
     * @param {View} view Accepts the view name
     * @param {Event} event Accepts the event object
     * @param {boolean} muteOnChange Accepts the value to enable or disable mute on change
     * @param {number} index Accepts the index value
     * @returns {void}
     * @private
     */
    Schedule.prototype.changeView = function (view, event, muteOnChange, index) {
        var _this = this;
        if (isNullOrUndefined(index)) {
            index = this.getViewIndex(view);
        }
        if (!muteOnChange && index === this.viewIndex && this.currentView === view || index < 0) {
            return;
        }
        var previousView = this.activeViewOptions ? this.activeViewOptions.option : this.currentView;
        var args = { requestType: 'viewNavigate', cancel: false, event: event };
        this.trigger(events.actionBegin, args, function (actionArgs) {
            if (!actionArgs.cancel) {
                var navArgs = {
                    action: 'view', cancel: false, currentDate: _this.selectedDate, previousView: previousView, currentView: view, viewIndex: index
                };
                _this.trigger(events.navigating, navArgs, function (navigationArgs) {
                    if (!navigationArgs.cancel) {
                        var isVertical = ['Day', 'Week', 'WorkWeek'].indexOf(view) > -1 && ['Day', 'Week', 'WorkWeek'].indexOf(previousView) < 0;
                        _this.uiStateValues.isInitial = isVertical || view.indexOf('Timeline') > -1 || view.indexOf('Year') > -1;
                        _this.uiStateValues.top = view.indexOf('Timeline') > -1 && previousView.indexOf('Timeline') < 0 ? 0 : _this.uiStateValues.top;
                        _this.viewIndex = navigationArgs.viewIndex;
                        _this.setProperties({ currentView: view }, true);
                        if (_this.headerModule) {
                            _this.headerModule.updateActiveView();
                            _this.headerModule.setCalendarDate(_this.selectedDate);
                            _this.headerModule.setCalendarView();
                        }
                        _this.initializeView(_this.currentView);
                        _this.animateLayout();
                        args = { requestType: 'viewNavigate', cancel: false, event: event };
                        _this.trigger(events.actionComplete, args);
                    }
                    else {
                        _this.currentView = previousView;
                    }
                });
            }
            else {
                _this.currentView = previousView;
            }
        });
    };
    /**
     * Method to change the view date
     *
     * @param {Date} selectedDate Accepts the selected date
     * @param {Event} event Accepts the event object
     * @returns {void}
     * @private
     */
    Schedule.prototype.changeDate = function (selectedDate, event) {
        var _this = this;
        var args = { requestType: 'dateNavigate', cancel: false, event: event };
        this.trigger(events.actionBegin, args, function (actionArgs) {
            if (!actionArgs.cancel) {
                var navArgs = {
                    action: 'date', cancel: false, previousDate: _this.selectedDate, currentDate: selectedDate
                };
                _this.trigger(events.navigating, navArgs, function (navigationArgs) {
                    if (!navigationArgs.cancel) {
                        _this.uiStateValues.isInitial = _this.activeView.isTimelineView() && _this.currentView !== 'TimelineYear';
                        _this.validateDate(navigationArgs.currentDate);
                        if (_this.headerModule) {
                            _this.headerModule.setCalendarDate(navigationArgs.currentDate);
                        }
                        if (_this.currentView === 'MonthAgenda' && _this.monthAgendaModule) {
                            _this.monthAgendaModule.monthAgendaDate = new Date('' + _this.selectedDate);
                        }
                        _this.initializeView(_this.currentView);
                        _this.animateLayout();
                        args = { requestType: 'dateNavigate', cancel: false, event: event };
                        _this.trigger(events.actionComplete, args);
                    }
                });
            }
        });
    };
    /**
     * Method to validate min and max date
     *
     * @param {Date} date Accepts the date object
     * @returns {boolean} Returns the boolean result to validate the min and max date
     * @private
     */
    Schedule.prototype.isMinMaxDate = function (date) {
        if (date === void 0) { date = this.selectedDate; }
        var maxDate = isNullOrUndefined(this.maxDate) ? new Date(2099, 11, 31) : this.maxDate;
        var minDate = isNullOrUndefined(this.minDate) ? new Date(1900, 0, 1) : this.minDate;
        return ((date.getTime() >= minDate.getTime()) && (date.getTime() <= maxDate.getTime()));
    };
    /**
     * Method to validate the selected date
     *
     * @param {Date} date Accepts the date object
     * @returns {boolean} Returns the boolean value for given date is selected date or not
     * @private
     */
    Schedule.prototype.isSelectedDate = function (date) {
        return date.setHours(0, 0, 0, 0) === new Date('' + this.selectedDate).setHours(0, 0, 0, 0);
    };
    /**
     * Method to get the current time
     *
     * @param {Date} date Accepts the date object
     * @returns {Date} Returns the date object after performing the timezone conversion
     * @private
     */
    Schedule.prototype.getCurrentTime = function (date) {
        if (date === void 0) { date = new Date(); }
        if (this.timezone) {
            return this.tzModule.convert(date, this.tzModule.getLocalTimezoneName(), this.timezone);
        }
        return date;
    };
    /** Method to get navigate view
     *
     * @returns {View} Return the navigate view name
     * @private
     */
    Schedule.prototype.getNavigateView = function () {
        if (this.activeView.isTimelineView()) {
            return this.currentView === 'TimelineMonth' || this.currentView === 'TimelineYear' ? 'TimelineDay' : 'Agenda';
        }
        return 'Day';
    };
    Schedule.prototype.animateLayout = function () {
        if (!this.activeView.element) {
            return;
        }
        new Animation({ duration: 600, name: 'FadeIn', timingFunction: 'easeIn' }).animate(this.activeView.element);
    };
    /**
     * To provide the array of modules needed for control rendering
     *
     * @returns {ModuleDeclaration[]} Returns the declared modules
     * @private
     */
    Schedule.prototype.requiredModules = function () {
        var modules = [];
        this.setViewOptions(true);
        for (var _i = 0, _a = Object.keys(this.viewOptions); _i < _a.length; _i++) {
            var view = _a[_i];
            view = (view === 'timelineDay' || view === 'timelineWeek' || view === 'timelineWorkWeek') ? 'timelineViews' : view;
            modules.push({ member: view, args: [this] });
        }
        if (this.allowDragAndDrop) {
            modules.push({ member: 'dragAndDrop', args: [this] });
        }
        if (this.allowResizing) {
            modules.push({ member: 'resize', args: [this] });
        }
        modules.push({ member: 'excelExport', args: [this] });
        modules.push({ member: 'iCalendarExport', args: [this] });
        modules.push({ member: 'iCalendarImport', args: [this] });
        modules.push({ member: 'print', args: [this] });
        return modules;
    };
    /**
     * Initializes the values of private members.
     *
     * @returns {void}
     * @private
     */
    Schedule.prototype.preRender = function () {
        this.isAdaptive = Browser.isDevice || util.isIPadDevice();
        this.globalize = new Internationalization(this.locale);
        this.tzModule = new Timezone();
        if (this && isNullOrUndefined(this.uiStateValues) || !(this.enablePersistence)) {
            this.uiStateValues = {
                expand: false, isInitial: true, left: 0, top: 0, isGroupAdaptive: false,
                isIgnoreOccurrence: false, groupIndex: this.adaptiveGroupIndex, action: false,
                isBlock: false, isCustomMonth: true, isPreventTimezone: false, isTransformed: false
            };
        }
        this.currentTimezoneDate = this.getCurrentTime();
        this.activeCellsData = {
            startTime: new Date(this.currentTimezoneDate),
            endTime: new Date(this.currentTimezoneDate),
            isAllDay: false
        };
        this.activeEventData = { event: undefined, element: undefined };
        this.getDefaultLocale();
        this.localeObj = new L10n(this.getModuleName(), this.defaultLocale, this.locale);
        this.setCldrTimeFormat();
        this.setCalendarMode();
        this.eventsData = [];
        this.eventsProcessed = [];
        this.blockData = [];
        this.blockProcessed = [];
        this.resourceCollection = [];
        this.currentAction = null;
        this.selectedElements = [];
        this.activeEventTemplates = [];
        this.setViewOptions();
    };
    Schedule.prototype.getDefaultLocale = function () {
        this.defaultLocale = {
            day: 'Day',
            week: 'Week',
            workWeek: 'Work Week',
            month: 'Month',
            year: 'Year',
            agenda: 'Agenda',
            weekAgenda: 'Week Agenda',
            workWeekAgenda: 'Work Week Agenda',
            monthAgenda: 'Month Agenda',
            today: 'Today',
            noEvents: 'No events',
            emptyContainer: 'There are no events scheduled on this day.',
            allDay: 'All day',
            start: 'Start',
            end: 'End',
            more: 'more',
            close: 'Close',
            cancel: 'Cancel',
            noTitle: '(No Title)',
            delete: 'Delete',
            deleteEvent: 'Delete Event',
            deleteMultipleEvent: 'Delete Multiple Events',
            selectedItems: 'Items selected',
            deleteSeries: 'Entire Series',
            edit: 'Edit',
            editSeries: 'Entire Series',
            editEvent: 'Edit Event',
            createEvent: 'Create',
            subject: 'Subject',
            addTitle: 'Add title',
            moreDetails: 'More Details',
            moreEvents: 'More Events',
            save: 'Save',
            editContent: 'How would you like to change the appointment in the series?',
            deleteContent: 'Are you sure you want to delete this event?',
            deleteMultipleContent: 'Are you sure you want to delete the selected events?',
            newEvent: 'New Event',
            title: 'Title',
            location: 'Location',
            description: 'Description',
            timezone: 'Timezone',
            startTimezone: 'Start Timezone',
            endTimezone: 'End Timezone',
            repeat: 'Repeat',
            saveButton: 'Save',
            cancelButton: 'Cancel',
            deleteButton: 'Delete',
            recurrence: 'Recurrence',
            wrongPattern: 'The recurrence pattern is not valid.',
            seriesChangeAlert: 'Do you want to cancel the changes made to specific ' +
                'instances of this series and match it to the whole series again?',
            createError: 'The duration of the event must be shorter than how frequently it occurs. ' +
                'Shorten the duration, or change the recurrence pattern in the recurrence event editor.',
            sameDayAlert: 'Two occurrences of the same event cannot occur on the same day.',
            occurenceAlert: 'Cannot reschedule an occurrence of the recurring appointment if it skips over ' +
                'a later occurrence of the same appointment.',
            editRecurrence: 'Edit Recurrence',
            recurringEvent: 'Recurring Event',
            repeats: 'Repeats',
            alert: 'Alert',
            startEndError: 'The selected end date occurs before the start date.',
            invalidDateError: 'The entered date value is invalid.',
            blockAlert: 'Events cannot be scheduled within the blocked time range.',
            ok: 'Ok',
            yes: 'Yes',
            no: 'No',
            of: 'of',
            occurrence: 'Occurrence',
            series: 'Series',
            previous: 'Previous',
            next: 'Next',
            timelineDay: 'Timeline Day',
            timelineWeek: 'Timeline Week',
            timelineWorkWeek: 'Timeline Work Week',
            timelineMonth: 'Timeline Month',
            timelineYear: 'Timeline Year',
            editFollowingEvent: 'Following Events',
            deleteTitle: 'Delete Event',
            editTitle: 'Edit Event',
            beginFrom: 'Begin From',
            endAt: 'Ends At',
            expandAllDaySection: 'Expand-all-day-section',
            collapseAllDaySection: 'Collapse-all-day-section',
            searchTimezone: 'Search Timezone',
            noRecords: 'No records found'
        };
    };
    Schedule.prototype.wireEvents = function () {
        EventHandler.add(window, 'resize', this.onScheduleResize, this);
        EventHandler.add(window, 'orientationchange', this.onScheduleResize, this);
        EventHandler.add(document, Browser.touchStartEvent, this.onDocumentClick, this);
    };
    /**
     * Method to remove selected class
     *
     * @returns {void}
     * @private
     */
    Schedule.prototype.removeSelectedClass = function () {
        var selectedCells = this.getSelectedCells();
        for (var _i = 0, selectedCells_1 = selectedCells; _i < selectedCells_1.length; _i++) {
            var cell = selectedCells_1[_i];
            if (this.currentView !== 'Year') {
                cell.setAttribute('aria-selected', 'false');
            }
            cell.removeAttribute('tabindex');
        }
        removeClass(selectedCells, cls.SELECTED_CELL_CLASS);
        if (this.keyboardInteractionModule && this.keyboardInteractionModule.selectedCells.length > 0) {
            this.keyboardInteractionModule.selectedCells = [];
        }
    };
    /**
     * Method to add selected class
     *
     * @param {HTMLTableCellElement[]} cells Accepts the collection of elements
     * @param {HTMLTableCellElement} focusCell Accepts the focus element
     * @param {boolean} isPreventScroll Accepts the boolean value to prevent scroll
     * @returns {void}
     * @private
     */
    Schedule.prototype.addSelectedClass = function (cells, focusCell, isPreventScroll) {
        if (this.currentView !== 'Year') {
            for (var _i = 0, cells_1 = cells; _i < cells_1.length; _i++) {
                var cell = cells_1[_i];
                cell.setAttribute('aria-selected', 'true');
            }
        }
        addClass(cells, cls.SELECTED_CELL_CLASS);
        if (focusCell) {
            focusCell.setAttribute('tabindex', '0');
            focusCell.focus({ preventScroll: isPreventScroll || false });
        }
    };
    /**
     * Method to select cell
     *
     * @param {HTMLElement | HTMLTableCellElement} element Accepts the select element
     * @returns {void}
     * @private
     */
    Schedule.prototype.selectCell = function (element) {
        this.removeSelectedClass();
        this.addSelectedClass([element], element);
    };
    /**
     * Method to get all day row element
     *
     * @returns {Element} Returns the all day row element
     * @private
     */
    Schedule.prototype.getAllDayRow = function () {
        return this.element.querySelector('.' + cls.ALLDAY_ROW_CLASS);
    };
    /**
     * Method to get content table element
     *
     * @returns {HTMLElement} Returns the content table element
     * @private
     */
    Schedule.prototype.getContentTable = function () {
        return this.activeView.element.querySelector('.' + cls.CONTENT_TABLE_CLASS + ' tbody');
    };
    /**
     * Method to get all content table rows
     *
     * @returns {HTMLElement[]} Returns the content table rows
     * @private
     */
    Schedule.prototype.getTableRows = function () {
        return [].slice.call(this.element.querySelectorAll('.' + cls.CONTENT_TABLE_CLASS + ' tbody tr:not(.' + cls.HIDDEN_CLASS + ')'));
    };
    /**
     * Method to get work cell elements
     *
     * @returns {Element[]} Returns the all work cell elements
     * @private
     */
    Schedule.prototype.getWorkCellElements = function () {
        return [].slice.call(this.element.querySelectorAll('.' + cls.WORK_CELLS_CLASS));
    };
    /**
     * Method to get the index from date collection
     *
     * @param {Date[]} collection Accepts the collections of date
     * @param {Date} date Accepts the date object
     * @returns {number} Returns the index compared date with date collections
     * @private
     */
    Schedule.prototype.getIndexOfDate = function (collection, date) {
        return collection.map(Number).indexOf(+date);
    };
    /**
     * Method to find all day cell
     *
     * @param {Element} td Accepts the DOM Element
     * @returns {boolean} Returns the boolean value
     * @private
     */
    Schedule.prototype.isAllDayCell = function (td) {
        if (['Month', 'TimelineMonth', 'TimelineYear', 'MonthAgenda'].indexOf(this.currentView) > -1 ||
            td.classList.contains(cls.ALLDAY_CELLS_CLASS) ||
            td.classList.contains(cls.HEADER_CELLS_CLASS) || !this.activeViewOptions.timeScale.enable) {
            return true;
        }
        if (this.activeView.isTimelineView() && this.activeViewOptions.headerRows.length > 0 &&
            this.activeViewOptions.headerRows.slice(-1)[0].option !== 'Hour') {
            return true;
        }
        return false;
    };
    /**
     * Method to get date from element
     *
     * @param {Element} td Accepts the DOM element
     * @returns {Date} Returns the date object
     * @private
     */
    Schedule.prototype.getDateFromElement = function (td) {
        var dateString;
        if (!isNullOrUndefined(td)) {
            dateString = td.getAttribute('data-date');
        }
        if (!isNullOrUndefined(dateString)) {
            var dateInMS = parseInt(dateString, 10);
            var date = new Date(dateInMS);
            return date;
        }
        return undefined;
    };
    /**
     * Method to get target element from given selector
     *
     * @param {string} selector Accepts the element selector
     * @param {number} left Accepts the pageX value
     * @param {number} top Accepts the pageY value
     * @returns {Element[]} Returns the collection of elements based on the given selector
     * @private
     */
    Schedule.prototype.getTargetElement = function (selector, left, top) {
        var element = document.elementFromPoint(left, top);
        var targetElement;
        if (element) {
            targetElement = element.closest(selector);
        }
        return (targetElement) ? [targetElement] : null;
    };
    /**
     * Method to process cell header template
     *
     * @returns {CallbackFunction} Returns the callback function
     * @private
     */
    Schedule.prototype.getCellHeaderTemplate = function () {
        return this.cellHeaderTemplateFn;
    };
    /**
     * Method to process cell header template in year view.
     *
     * @returns {CallbackFunction} Returns the callback function
     * @private
     */
    Schedule.prototype.getDayHeaderTemplate = function () {
        return this.dayHeaderTemplateFn;
    };
    /**
     * Method to process cell header template in year view.
     *
     * @returns {CallbackFunction} Returns the callback function
     * @private
     */
    Schedule.prototype.getMonthHeaderTemplate = function () {
        return this.monthHeaderTemplateFn;
    };
    /**
     * Method to process cell template
     *
     * @returns {CallbackFunction} Returns the callback function
     * @private
     */
    Schedule.prototype.getCellTemplate = function () {
        return this.cellTemplateFn;
    };
    /**
     * Method to process date header template
     *
     * @returns {CallbackFunction} Returns the callback function
     * @private
     */
    Schedule.prototype.getDateHeaderTemplate = function () {
        return this.dateHeaderTemplateFn;
    };
    /**
     * Method to process date range template
     *
     * @returns {CallbackFunction} Returns the callback function
     * @private
     */
    Schedule.prototype.getDateRangeTemplate = function () {
        return this.dateRangeTemplateFn;
    };
    /**
     * Method to process major slot template
     *
     * @returns {CallbackFunction} Returns the callback function
     * @private
     */
    Schedule.prototype.getMajorSlotTemplate = function () {
        return this.majorSlotTemplateFn;
    };
    /**
     * Method to process minor slot template
     *
     * @returns {CallbackFunction} Returns the callback function
     * @private
     */
    Schedule.prototype.getMinorSlotTemplate = function () {
        return this.minorSlotTemplateFn;
    };
    /**
     * Method to process appointment template
     *
     * @returns {CallbackFunction} Returns the callback function
     * @private
     */
    Schedule.prototype.getAppointmentTemplate = function () {
        return this.appointmentTemplateFn;
    };
    /**
     * Method to process appointment tooltip template
     *
     * @returns {CallbackFunction} Returns the callback function
     * @private
     */
    Schedule.prototype.getEventTooltipTemplate = function () {
        return this.eventTooltipTemplateFn;
    };
    /**
     * Method to process header tooltip template
     *
     * @returns {CallbackFunction} Returns the callback function
     * @private
     */
    Schedule.prototype.getHeaderTooltipTemplate = function () {
        return this.headerTooltipTemplateFn;
    };
    /**
     * Method to process editor template
     *
     * @returns {CallbackFunction} Returns the callback function
     * @private
     */
    Schedule.prototype.getEditorTemplate = function () {
        return this.editorTemplateFn;
    };
    /**
     * Method to process editor header template
     *
     * @returns {CallbackFunction} Returns the callback function
     * @private
     */
    Schedule.prototype.getEditorHeaderTemplate = function () {
        return this.editorHeaderTemplateFn;
    };
    /**
     * Method to process editor footer template
     *
     * @returns {CallbackFunction} Returns the callback function
     * @private
     */
    Schedule.prototype.getEditorFooterTemplate = function () {
        return this.editorFooterTemplateFn;
    };
    /**
     * Method to process quick info header template
     *
     * @returns {CallbackFunction} Returns the callback function
     * @private
     */
    Schedule.prototype.getQuickInfoTemplatesHeader = function () {
        return this.quickInfoTemplatesHeaderFn;
    };
    /**
     * Method to process quick info content template
     *
     * @returns {CallbackFunction} Returns the callback function
     * @private
     */
    Schedule.prototype.getQuickInfoTemplatesContent = function () {
        return this.quickInfoTemplatesContentFn;
    };
    /**
     * Method to process quick info footer template
     *
     * @returns {CallbackFunction} Returns the callback function
     * @private
     */
    Schedule.prototype.getQuickInfoTemplatesFooter = function () {
        return this.quickInfoTemplatesFooterFn;
    };
    /**
     * Method to process resource header template
     *
     * @returns {CallbackFunction} Returns the callback function
     * @private
     */
    Schedule.prototype.getResourceHeaderTemplate = function () {
        return this.resourceHeaderTemplateFn;
    };
    /**
     * Method to process indent template
     *
     * @returns {CallbackFunction} Returns the callback function
     * @private
     */
    Schedule.prototype.getHeaderIndentTemplate = function () {
        return this.headerIndentTemplateFn;
    };
    /**
     * Method to get dynamic CSS properties
     *
     * @returns {ScrollCss} Returns the CSS properties dynamically
     * @private
     */
    Schedule.prototype.getCssProperties = function () {
        var cssProps = {
            border: this.enableRtl ? 'borderLeftWidth' : 'borderRightWidth',
            padding: this.enableRtl ? 'paddingLeft' : 'paddingRight',
            rtlBorder: this.enableRtl ? 'borderRightWidth' : 'borderLeftWidth',
            rtlPadding: this.enableRtl ? 'paddingRight' : 'paddingLeft'
        };
        return cssProps;
    };
    /**
     * Method to remove new event element in adaptive mode
     *
     * @returns {void}
     * @private
     */
    Schedule.prototype.removeNewEventElement = function () {
        var eventClone = this.element.querySelector('.' + cls.NEW_EVENT_CLASS);
        if (!isNullOrUndefined(eventClone)) {
            remove(eventClone);
        }
    };
    /**
     * Method to get start end time from string
     *
     * @param {string} startEndTime Accepts the start end time string value
     * @returns {Date} Returns the date object
     * @private
     */
    Schedule.prototype.getStartEndTime = function (startEndTime) {
        if (!isNullOrUndefined(startEndTime) && startEndTime !== '') {
            var startEndDate = util.resetTime(new Date(this.currentTimezoneDate) || this.getCurrentTime());
            var timeString = startEndTime.split(':');
            if (timeString.length === 2) {
                startEndDate.setHours(parseInt(timeString[0], 10), parseInt(timeString[1], 10), 0);
            }
            return startEndDate;
        }
        return null;
    };
    Schedule.prototype.onDocumentClick = function (args) {
        this.notify(events.documentClick, { event: args });
    };
    Schedule.prototype.onScheduleResize = function () {
        if (isNullOrUndefined(this.activeView) || ((this.isAdaptive || util.isMobile()) && document.activeElement
            && (document.activeElement.classList.contains(cls.SUBJECT_CLASS) ||
                document.activeElement.classList.contains(cls.INLINE_SUBJECT_CLASS))) || this.uiStateValues.isTapHold) {
            return;
        }
        if (this.virtualScrollModule && this.activeView.isTimelineView()) {
            this.virtualScrollModule.refreshLayout();
        }
        if (this.activeViewOptions.timeScale.enable && this.activeView) {
            this.activeView.highlightCurrentTime();
        }
        if (this.quickPopup) {
            this.quickPopup.onClosePopup();
        }
        util.resetScrollbarWidth();
        if (this.currentView === 'Month' || ((this.currentView !== 'Agenda' && this.currentView !== 'MonthAgenda')
            && !this.activeViewOptions.timeScale.enable) || this.activeView.isTimelineView()) {
            this.activeView.resetColWidth();
            this.notify(events.scrollUiUpdate, { cssProperties: this.getCssProperties(), isPreventScrollUpdate: true });
            var isRemoteRefresh = false;
            if (this.activeViewOptions.enableLazyLoading && this.virtualScrollModule && this.virtualScrollModule.isRemoteRefresh) {
                isRemoteRefresh = this.virtualScrollModule.isRemoteRefresh;
                this.virtualScrollModule.isRemoteRefresh = false;
            }
            this.refreshEvents(isRemoteRefresh);
            if (this.virtualScrollModule && !this.virtualScrollModule.enableTransition) {
                var resWrap = this.element.querySelector('.' + cls.RESOURCE_COLUMN_WRAP_CLASS);
                var conWrap = this.element.querySelector('.' + cls.CONTENT_WRAP_CLASS);
                this.virtualScrollModule.enableTransition = true;
                addClass([conWrap, resWrap], 'e-transition');
            }
        }
        else {
            this.notify(events.contentReady, {});
        }
    };
    /**
     * Method to process the templates
     *
     * @param {string | Function} template Accepts the template in string
     * @returns {CallbackFunction} Returns the callback function
     * @private
     */
    Schedule.prototype.templateParser = function (template) {
        if (template) {
            try {
                if (typeof template === 'function') {
                    return compile(template);
                }
                else {
                    if (document.querySelectorAll(template).length) {
                        return compile(document.querySelector(template).innerHTML.trim());
                    }
                    else {
                        return compile(template);
                    }
                }
            }
            catch (error) {
                return compile(template);
            }
        }
        return undefined;
    };
    /**
     * Retrieves the selected cells.
     *
     * @returns {Element[]} The elements of currently selected cells will be returned.
     * @private
     */
    Schedule.prototype.getSelectedCells = function () {
        return [].slice.call(this.element.querySelectorAll('.' + cls.SELECTED_CELL_CLASS));
    };
    /**
     * Method to generate the announcement string
     *
     * @param {Object} event Accepts the event object
     * @param {string} subject Accepts the subject text
     * @returns {string} Returns the announcement string
     * @private
     */
    Schedule.prototype.getAnnouncementString = function (event, subject) {
        var resourceName;
        if (this.quickPopup && this.activeViewOptions.group.resources.length > 0) {
            var constantText = '"s event - ';
            resourceName = this.quickPopup.getResourceText({ event: event }, 'event') + constantText;
        }
        var recordSubject = (subject || (event[this.eventFields.subject] || this.eventSettings.fields.subject.default));
        var recordLocation = (event[this.eventFields.location] || this.eventSettings.fields.location.default);
        var skeleton = 'full';
        var startDateText = this.globalize.formatDate(event[this.eventFields.startTime], {
            type: 'dateTime', skeleton: skeleton, calendar: this.getCalendarMode()
        });
        var endDateText = this.globalize.formatDate(event[this.eventFields.endTime], {
            type: 'dateTime', skeleton: skeleton, calendar: this.getCalendarMode()
        });
        var announcementString = recordSubject + ' ' + this.localeObj.getConstant('beginFrom') + ' '
            + startDateText + ' ' + this.localeObj.getConstant('endAt') + ' ' + endDateText;
        if (resourceName) {
            announcementString = resourceName + ' ' + announcementString;
        }
        if (recordLocation && recordLocation !== '') {
            announcementString = announcementString + ' ' + this.localeObj.getConstant('location') + ' ' + recordLocation;
        }
        if (event[this.eventFields.recurrenceRule] && event[this.eventFields.recurrenceRule] !== ''
            && event[this.eventFields.id] === event[this.eventFields.recurrenceID]) {
            announcementString = announcementString + ' ' + this.localeObj.getConstant('recurringEvent');
        }
        return announcementString;
    };
    /**
     * Method to process the element boundary validation
     *
     * @param {number} pageY Accepts the pageY value
     * @param {number} pageX Accepts the pageX value
     * @returns {ResizeEdges} Returns the boundary validation object
     * @private
     */
    Schedule.prototype.boundaryValidation = function (pageY, pageX) {
        var autoScrollDistance = 30;
        var scrollEdges = { left: false, right: false, top: false, bottom: false };
        var viewBoundaries = this.element.querySelector('.' + cls.CONTENT_WRAP_CLASS).getBoundingClientRect();
        if ((pageY < viewBoundaries.top + autoScrollDistance + window.pageYOffset) &&
            (pageY > viewBoundaries.top + window.pageYOffset)) {
            scrollEdges.top = true;
        }
        if ((pageY > (viewBoundaries.bottom - autoScrollDistance) + window.pageYOffset) &&
            (pageY < viewBoundaries.bottom + window.pageYOffset)) {
            scrollEdges.bottom = true;
        }
        if ((pageX < viewBoundaries.left + autoScrollDistance + window.pageXOffset) &&
            (pageX > viewBoundaries.left + window.pageXOffset)) {
            scrollEdges.left = true;
        }
        if ((pageX > (viewBoundaries.right - autoScrollDistance) + window.pageXOffset) &&
            (pageX < viewBoundaries.right + window.pageXOffset)) {
            scrollEdges.right = true;
        }
        return scrollEdges;
    };
    /**
     * Method to get the week number.
     *
     * @param {Date[]} dates Accepts the date collections
     * @returns {number} Returns the week number
     * @private
     */
    Schedule.prototype.getWeekNumberContent = function (dates) {
        var weekNumber;
        if (this.weekRule === 'FirstDay') {
            var weekNumberDate = util.getWeekLastDate(dates.slice(-1)[0], this.firstDayOfWeek);
            weekNumber = this.globalize.formatNumber(util.getWeekNumber(weekNumberDate));
        }
        else if (this.weekRule === 'FirstFourDayWeek') {
            var weekFirstDate = util.getWeekFirstDate(dates.slice(-1)[0], this.firstDayOfWeek);
            var weekLastDate = util.getWeekLastDate(dates.slice(-1)[0], this.firstDayOfWeek);
            var weekMidDate = util.getWeekMiddleDate(weekFirstDate, weekLastDate);
            weekNumber = this.globalize.formatNumber(util.getWeekNumber(weekMidDate));
        }
        else if (this.weekRule === 'FirstFullWeek') {
            var weekFirstDate = util.getWeekFirstDate(dates.slice(-1)[0], this.firstDayOfWeek);
            weekNumber = this.globalize.formatNumber(util.getWeekNumber(weekFirstDate));
        }
        return weekNumber;
    };
    /**
     * Method to render the header indent template.
     *
     * @param {TdData} data Accepts the td data
     * @param {Element} td Accepts the td element
     * @returns {void}
     * @private
     */
    Schedule.prototype.renderHeaderIndentTemplate = function (data, td) {
        if (this.activeViewOptions.headerIndentTemplate) {
            var scheduleId = this.element.id + '_';
            var viewName = this.activeViewOptions.headerIndentTemplateName;
            var templateId = scheduleId + viewName + 'headerIndentTemplate';
            var indentTemplate = [].slice.call(this.getHeaderIndentTemplate()(data, this, 'headerIndentTemplate', templateId, false, undefined, undefined, this.root));
            append(indentTemplate, td);
        }
    };
    /**
     * Method to check for refreshing the targeted resource row events.
     *
     * @returns {boolean} Returns the boolean value
     * @private
     */
    Schedule.prototype.isSpecificResourceEvents = function () {
        return this.activeViewOptions.group.resources.length > 0 && !this.activeViewOptions.group.allowGroupEdit &&
            !this.rowAutoHeight && !this.virtualScrollModule && this.activeViewOptions.group.byGroupID;
    };
    Schedule.prototype.unWireEvents = function () {
        EventHandler.remove(window, 'resize', this.onScheduleResize);
        EventHandler.remove(window, 'orientationchange', this.onScheduleResize);
        EventHandler.remove(document, Browser.touchStartEvent, this.onDocumentClick);
    };
    /**
     * Core method to return the component name.
     *
     * @returns {string} Returns the module name
     * @private
     */
    Schedule.prototype.getModuleName = function () {
        return 'schedule';
    };
    /**
     * Returns the properties to be maintained in the persisted state.
     *
     * @returns {string} Returns the persistance data
     * @private
     */
    Schedule.prototype.getPersistData = function () {
        return this.addOnPersist(['currentView', 'selectedDate', 'scrollTop', 'scrollLeft', 'adaptiveGroupIndex']);
    };
    /**
     * Called internally, if any of the property value changed.
     *
     * @returns {void}
     * @private
     */
    Schedule.prototype.onPropertyChanged = function (newProp, oldProp) {
        if (this.isReact && isNullOrUndefined(this.activeView)) {
            return;
        }
        var state = {
            isRefresh: false, isResource: false, isDate: false, isView: false, isLayout: false, isDataManager: false
        };
        for (var _i = 0, _a = Object.keys(newProp); _i < _a.length; _i++) {
            var prop = _a[_i];
            switch (prop) {
                case 'views':
                    this.setViewOptions();
                    if (this.headerModule) {
                        this.headerModule.updateItems();
                    }
                    state.isView = true;
                    break;
                case 'currentView':
                    state.isView = true;
                    break;
                case 'minDate':
                case 'maxDate':
                case 'selectedDate':
                    state.isDate = true;
                    break;
                case 'dateFormat':
                    this.activeViewOptions = this.getActiveViewOptions();
                    if (this.headerModule) {
                        this.headerModule.updateDateRange();
                    }
                    break;
                case 'showHeaderBar':
                case 'toolbarItems':
                    this.destroyHeaderModule();
                    if (newProp.showHeaderBar) {
                        this.headerModule = new HeaderRenderer(this);
                        this.headerModule.updateDateRange();
                    }
                    this.notify(events.scrollUiUpdate, { cssProperties: this.getCssProperties() });
                    if (this.activeView.isTimelineView()) {
                        this.refreshEvents(false);
                    }
                    break;
                case 'workDays':
                    if (JSON.stringify(oldProp.workDays) !== JSON.stringify(newProp.workDays)) {
                        state.isLayout = true;
                    }
                    break;
                case 'showWeekend':
                case 'startHour':
                case 'endHour':
                case 'workHours':
                case 'readonly':
                case 'headerRows':
                case 'showWeekNumber':
                case 'rowAutoHeight':
                    state.isLayout = true;
                    break;
                case 'locale':
                case 'calendarMode':
                    this.globalize = new Internationalization(this.locale);
                    this.localeObj = new L10n(this.getModuleName(), this.defaultLocale, this.locale);
                    this.setCldrTimeFormat();
                    this.setCalendarMode();
                    state.isRefresh = true;
                    break;
                case 'firstDayOfWeek':
                    this.activeViewOptions.firstDayOfWeek = newProp.firstDayOfWeek;
                    if (this.eventWindow) {
                        this.eventWindow.refreshRecurrenceEditor();
                    }
                    state.isLayout = true;
                    break;
                case 'showTimeIndicator':
                    if (this.activeViewOptions.timeScale.enable && this.activeView) {
                        this.activeView.highlightCurrentTime();
                    }
                    break;
                case 'cellHeaderTemplate':
                    this.activeViewOptions.cellHeaderTemplate = newProp.cellHeaderTemplate;
                    this.cellHeaderTemplateFn = this.templateParser(this.activeViewOptions.cellHeaderTemplate);
                    state.isLayout = true;
                    break;
                case 'cellTemplate':
                    this.activeViewOptions.cellTemplate = newProp.cellTemplate;
                    this.cellTemplateFn = this.templateParser(this.activeViewOptions.cellTemplate);
                    state.isLayout = true;
                    break;
                case 'dateHeaderTemplate':
                    this.activeViewOptions.dateHeaderTemplate = newProp.dateHeaderTemplate;
                    this.dateHeaderTemplateFn = this.templateParser(this.activeViewOptions.dateHeaderTemplate);
                    state.isLayout = true;
                    break;
                case 'dateRangeTemplate':
                    this.activeViewOptions.dateRangeTemplate = newProp.dateRangeTemplate;
                    this.dateRangeTemplateFn = this.templateParser(this.activeViewOptions.dateRangeTemplate);
                    if (this.headerModule) {
                        this.headerModule.updateDateRange();
                    }
                    break;
                case 'dayHeaderTemplate':
                    this.activeViewOptions.dayHeaderTemplate = newProp.dayHeaderTemplate;
                    this.dayHeaderTemplateFn = this.templateParser(this.activeViewOptions.dayHeaderTemplate);
                    state.isLayout = true;
                    break;
                case 'monthHeaderTemplate':
                    this.activeViewOptions.monthHeaderTemplate = newProp.monthHeaderTemplate;
                    this.monthHeaderTemplateFn = this.templateParser(this.activeViewOptions.monthHeaderTemplate);
                    state.isLayout = true;
                    break;
                case 'resourceHeaderTemplate':
                    this.activeViewOptions.resourceHeaderTemplate = newProp.resourceHeaderTemplate;
                    this.resourceHeaderTemplateFn = this.templateParser(this.activeViewOptions.resourceHeaderTemplate);
                    state.isLayout = true;
                    break;
                case 'timezone':
                    this.eventBase.timezonePropertyChange(oldProp.timezone);
                    if (this.headerModule) {
                        this.headerModule.setCalendarTimezone();
                    }
                    break;
                case 'enableRtl':
                    this.setRtlClass();
                    state.isRefresh = true;
                    break;
                default:
                    this.extendedPropertyChange(prop, newProp, oldProp, state);
                    break;
            }
        }
        this.propertyChangeAction(state);
    };
    Schedule.prototype.propertyChangeAction = function (state) {
        if (state.isRefresh) {
            this.refresh();
        }
        else if (state.isResource) {
            this.initializeResources(true);
        }
        else if (state.isView) {
            this.changeView(this.currentView, null, true);
        }
        else if (state.isDate) {
            if (isNullOrUndefined(this.selectedDate)) {
                this.setProperties({ selectedDate: this.getCurrentTime() }, true);
            }
            this.changeDate(this.selectedDate);
        }
        else if (state.isLayout) {
            this.activeCellsData = null;
            this.initializeView(this.currentView);
        }
        else if (state.isDataManager && this.crudModule) {
            if (this.dragAndDropModule) {
                this.dragAndDropModule.actionObj.action = '';
                removeClass([this.element], cls.EVENT_ACTION_CLASS);
            }
            this.crudModule.refreshDataManager();
        }
    };
    Schedule.prototype.allDayRowScrollUpdate = function () {
        var dateHeader = this.element.querySelector('.' + cls.DATE_HEADER_WRAP_CLASS);
        var allDayRow = this.element.querySelector('.' + cls.ALLDAY_ROW_CLASS);
        if (this.height === 'auto' || !this.enableAllDayScroll) {
            addClass([dateHeader], cls.ALLDAY_APPOINTMENT_AUTO);
            if (dateHeader.classList.contains(cls.ALLDAY_APPOINTMENT_SCROLL)) {
                removeClass([dateHeader], cls.ALLDAY_APPOINTMENT_SCROLL);
            }
            if (this.uiStateValues.expand) {
                var allDayCells = [].slice.call(this.element.querySelectorAll('.' + cls.ALLDAY_CELLS_CLASS));
                allDayCells[0].style.height = (allDayRow.style.height) ? allDayRow.style.height : allDayCells[1].style.height;
            }
        }
        else {
            if (dateHeader.classList.contains(cls.ALLDAY_APPOINTMENT_AUTO)) {
                removeClass([dateHeader], cls.ALLDAY_APPOINTMENT_AUTO);
            }
            this.eventBase.allDayExpandScroll(dateHeader);
        }
        if (!this.uiStateValues.expand) {
            allDayRow.style.height = '';
        }
    };
    Schedule.prototype.extendedPropertyChange = function (prop, newProp, oldProp, state) {
        switch (prop) {
            case 'width':
            case 'height':
            case 'enableAllDayScroll':
                if (['Day', 'Week', 'WorkWeek'].indexOf(this.currentView) > -1) {
                    this.allDayRowScrollUpdate();
                }
                this.notify(events.uiUpdate, { module: 'scroll', properties: { width: newProp.width, height: newProp.height } });
                break;
            case 'cssClass':
                if (oldProp.cssClass) {
                    removeClass([this.element], oldProp.cssClass.split(' '));
                }
                if (newProp.cssClass) {
                    addClass([this.element], newProp.cssClass.split(' '));
                }
                break;
            case 'hideEmptyAgendaDays':
            case 'agendaDaysCount':
                this.activeViewOptions = this.getActiveViewOptions();
                state.isView = true;
                break;
            case 'eventSettings':
                this.onEventSettingsPropertyChanged(newProp.eventSettings, oldProp.eventSettings, state);
                break;
            case 'allowKeyboardInteraction':
                if (this.keyboardInteractionModule) {
                    this.keyboardInteractionModule.destroy();
                    this.keyboardInteractionModule = null;
                }
                if (newProp.allowKeyboardInteraction) {
                    this.keyboardInteractionModule = new KeyboardInteraction(this);
                }
                break;
            case 'timezoneDataSource':
                if (this.eventWindow) {
                    this.eventWindow.refresh();
                }
                break;
            case 'editorTemplate':
                if (!isNullOrUndefined(this.editorTemplate)) {
                    this.editorTemplateFn = this.templateParser(this.editorTemplate);
                }
                if (this.eventWindow) {
                    this.eventWindow.setDialogContent();
                }
                break;
            case 'editorHeaderTemplate':
                if (!isNullOrUndefined(this.editorHeaderTemplate)) {
                    this.editorHeaderTemplateFn = this.templateParser(this.editorHeaderTemplate);
                }
                if (this.eventWindow) {
                    this.eventWindow.setDialogHeader();
                }
                break;
            case 'editorFooterTemplate':
                if (!isNullOrUndefined(this.editorFooterTemplate)) {
                    this.editorFooterTemplateFn = this.templateParser(this.editorFooterTemplate);
                }
                if (this.eventWindow) {
                    this.eventWindow.setDialogFooter();
                }
                break;
            case 'quickInfoTemplates':
                if (this.quickInfoTemplates.header) {
                    this.quickInfoTemplatesHeaderFn = this.templateParser(this.quickInfoTemplates.header);
                }
                if (this.quickInfoTemplates.content) {
                    this.quickInfoTemplatesContentFn = this.templateParser(this.quickInfoTemplates.content);
                }
                if (this.quickInfoTemplates.footer) {
                    this.quickInfoTemplatesFooterFn = this.templateParser(this.quickInfoTemplates.footer);
                }
                break;
            case 'group':
                this.onGroupSettingsPropertyChanged(newProp.group, oldProp.group, state);
                break;
            case 'resources':
                state.isResource = true;
                break;
            case 'timeScale':
                this.activeViewOptions.timeScale.interval = newProp.timeScale.interval || this.activeViewOptions.timeScale.interval;
                this.activeViewOptions.timeScale.slotCount = newProp.timeScale.slotCount || this.activeViewOptions.timeScale.slotCount;
                if (this.eventWindow) {
                    this.eventWindow.refreshDateTimePicker();
                }
                state.isLayout = true;
                break;
            case 'allowDragAndDrop':
            case 'allowResizing':
            case 'eventDragArea':
                this.refreshEvents(false);
                break;
            case 'weekRule':
                state.isLayout = true;
                break;
            case 'firstMonthOfYear':
                this.activeViewOptions.firstMonthOfYear = newProp.firstMonthOfYear;
                this.viewIndex = this.activeView.viewIndex;
                state.isLayout = true;
                break;
            case 'monthsCount':
                this.activeViewOptions.monthsCount = newProp.monthsCount;
                this.viewIndex = this.activeView.viewIndex;
                state.isLayout = true;
                break;
            case 'timeFormat':
                this.internalTimeFormat = newProp.timeFormat || this.activeViewOptions.timeFormat;
                if (this.eventWindow) {
                    this.eventWindow.refreshDateTimePicker();
                }
                state.isLayout = true;
                break;
            case 'enableAdaptiveUI':
                if (this.showHeaderBar && this.headerModule) {
                    this.destroyHeaderModule();
                    this.headerModule = new HeaderRenderer(this);
                    this.headerModule.updateDateRange();
                }
                state.isLayout = true;
                break;
            case 'headerIndentTemplate':
                this.activeViewOptions.headerIndentTemplate = newProp.headerIndentTemplate;
                this.headerIndentTemplateFn = this.templateParser(this.activeViewOptions.headerIndentTemplate);
                state.isLayout = true;
                break;
        }
    };
    Schedule.prototype.setRtlClass = function () {
        if (this.enableRtl) {
            addClass([this.element], 'e-rtl');
        }
        else {
            removeClass([this.element], 'e-rtl');
        }
    };
    Schedule.prototype.onGroupSettingsPropertyChanged = function (newProp, oldProp, state) {
        for (var _i = 0, _a = Object.keys(newProp); _i < _a.length; _i++) {
            var prop = _a[_i];
            if (prop === 'headerTooltipTemplate') {
                this.headerTooltipTemplateFn = this.templateParser(newProp.headerTooltipTemplate);
            }
            else {
                state.isLayout = true;
                if (this.eventWindow) {
                    this.eventWindow.refresh();
                }
            }
        }
    };
    Schedule.prototype.onEventSettingsPropertyChanged = function (newProp, oldProp, state) {
        for (var _i = 0, _a = Object.keys(newProp); _i < _a.length; _i++) {
            var prop = _a[_i];
            switch (prop) {
                case 'dataSource':
                case 'query':
                case 'fields':
                case 'includeFiltersInQuery':
                    this.initializeDataModule();
                    state.isDataManager = true;
                    break;
                case 'template':
                    this.activeViewOptions.eventTemplate = newProp.template;
                    this.appointmentTemplateFn = this.templateParser(this.activeViewOptions.eventTemplate);
                    state.isDataManager = true;
                    break;
                case 'enableTooltip':
                    if (this.eventTooltip) {
                        this.eventTooltip.destroy();
                        this.eventTooltip = null;
                    }
                    if (newProp.enableTooltip) {
                        this.eventTooltip = new EventTooltip(this);
                    }
                    break;
                case 'tooltipTemplate':
                    this.eventTooltipTemplateFn = this.templateParser(this.eventSettings.tooltipTemplate);
                    break;
                case 'resourceColorField':
                    if (this.resourceBase) {
                        this.resourceBase.setResourceCollection();
                    }
                    state.isDataManager = true;
                    break;
                case 'editFollowingEvents':
                    if (this.quickPopup) {
                        this.quickPopup.refreshQuickDialog();
                    }
                    break;
                case 'allowAdding':
                case 'allowEditing':
                case 'allowDeleting':
                    if (this.showHeaderBar && this.headerModule) {
                        this.headerModule.updateAddIcon();
                    }
                    if (this.eventWindow) {
                        this.eventWindow.refresh();
                    }
                    break;
                case 'spannedEventPlacement':
                case 'minimumEventDuration':
                case 'enableMaxHeight':
                case 'enableIndicator':
                    this.refreshEvents(false);
                    break;
                case 'ignoreWhitespace':
                    state.isLayout = true;
                    break;
            }
        }
    };
    Schedule.prototype.destroyHeaderModule = function () {
        if (this.headerModule) {
            this.headerModule.destroy();
            this.headerModule = null;
        }
    };
    Schedule.prototype.destroyPopups = function () {
        if (this.quickPopup) {
            this.quickPopup.destroy();
            this.quickPopup = null;
        }
        if (this.eventWindow) {
            this.eventWindow.destroy();
            this.eventWindow = null;
        }
    };
    /**
     * Allows to show the spinner on schedule at the required scenarios.
     *
     * @function showSpinner
     * @returns {void}
     */
    Schedule.prototype.showSpinner = function () {
        showSpinner(this.element);
    };
    /**
     * When the spinner is shown manually using `showSpinner` method, it can be hidden using this `hideSpinner` method.
     *
     * @function hideSpinner
     * @returns {void}
     */
    Schedule.prototype.hideSpinner = function () {
        hideSpinner(this.element);
    };
    /**
     * Sets different working hours on the required working days by accepting the required start and end time as well as the date collection
     *  as its parameters.
     *
     * @function setWorkHours
     * @param {Date} dates Collection of dates on which the given start and end hour range needs to be applied.
     * @param {string} start Defines the work start hour.
     * @param {string} end Defines the work end hour.
     * @param {number} groupIndex Defines the resource index from last level.
     * @returns {void}
     */
    Schedule.prototype.setWorkHours = function (dates, start, end, groupIndex) {
        var cells = [];
        cells = this.getWorkHourCells(dates, start, end, groupIndex);
        addClass(cells, cls.WORK_HOURS_CLASS);
    };
    /**
     * Removes or resets different working hours on the required working days by accepting the required start and end time as well as the
     * date collection as its parameters.
     * if no parameters has been passed to this function, it will remove all the work hours.
     *
     * @param {Date} dates Collection of dates on which the given start and end hour range need to be applied.
     * @param {string} start Defines the work start hour.
     * @param {string} end Defines the work end hour.
     * @param {number} groupIndex Defines the resource index from last level.
     * @returns {void}
     */
    Schedule.prototype.resetWorkHours = function (dates, start, end, groupIndex) {
        if (dates === void 0) { dates = this.activeView.renderDates; }
        if (dates && start && end) {
            var cells = this.getWorkHourCells(dates, start, end, groupIndex);
            removeClass(cells, cls.WORK_HOURS_CLASS);
        }
        else {
            var workHourCells = [].slice.call(this.element.querySelectorAll('.' + cls.WORK_HOURS_CLASS));
            removeClass(workHourCells, cls.WORK_HOURS_CLASS);
        }
    };
    Schedule.prototype.getWorkHourCells = function (dates, start, end, groupIndex) {
        if (['Agenda', 'MonthAgenda', 'Month', 'TimelineMonth'].indexOf(this.currentView) > -1) {
            return [];
        }
        var startHour = this.getStartEndTime(start);
        var endHour = this.getStartEndTime(end);
        var tableEle = this.getContentTable();
        if (isNullOrUndefined(startHour) || isNullOrUndefined(endHour) || !tableEle) {
            return [];
        }
        startHour.setMilliseconds(0);
        endHour.setMilliseconds(0);
        var viewStartHour = this.activeView.getStartHour();
        if (startHour < viewStartHour) {
            startHour = viewStartHour;
        }
        var viewEndHour = this.activeView.getEndHour();
        if (endHour > viewEndHour) {
            endHour = viewEndHour;
        }
        var msMajorInterval = this.activeViewOptions.timeScale.interval * util.MS_PER_MINUTE;
        var msInterval = msMajorInterval / this.activeViewOptions.timeScale.slotCount;
        var offsetDiff = ((viewStartHour.getTimezoneOffset() - startHour.getTimezoneOffset()) * util.MS_PER_MINUTE);
        var startIndex = Math.round((startHour.getTime() - viewStartHour.getTime() + offsetDiff) / msInterval);
        var endIndex = Math.ceil((endHour.getTime() - viewStartHour.getTime() + offsetDiff) / msInterval);
        var tempStartIndex = startIndex;
        var tempEndIndex = endIndex;
        var cells = [];
        for (var _i = 0, dates_1 = dates; _i < dates_1.length; _i++) {
            var date = dates_1[_i];
            date = this.getDateTime(date);
            util.resetTime(date);
            var renderDates = this.activeView.renderDates;
            if (!isNullOrUndefined(groupIndex) && this.resourceBase && !this.activeView.isTimelineView()) {
                renderDates = this.resourceBase.lastResourceLevel[parseInt(groupIndex.toString(), 10)].renderDates;
            }
            var colIndex = this.getIndexOfDate(renderDates, date);
            if (colIndex >= 0) {
                if (this.activeView.isTimelineView()) {
                    var slotsPerDay = Math.round((viewEndHour.getTime() - viewStartHour.getTime()) / msInterval);
                    startIndex = tempStartIndex + (colIndex * slotsPerDay);
                    endIndex = tempEndIndex + (colIndex * slotsPerDay);
                }
                for (var i = startIndex; i < endIndex; i++) {
                    if (this.activeView.isTimelineView()) {
                        var rowIndex = (!isNullOrUndefined(groupIndex)) ? groupIndex : 0;
                        cells.push(tableEle.rows[parseInt(rowIndex.toString(), 10)].cells[parseInt(i.toString(), 10)]);
                    }
                    else {
                        if (!isNullOrUndefined(groupIndex)) {
                            var selector = '.' + cls.WORK_CELLS_CLASS + '[data-group-index="' + groupIndex + '"]';
                            var tds = [].slice.call(tableEle.rows[parseInt(i.toString(), 10)].querySelectorAll(selector));
                            cells.push(tds[parseInt(colIndex.toString(), 10)]);
                        }
                        else {
                            cells.push(tableEle.rows[parseInt(i.toString(), 10)].cells[parseInt(colIndex.toString(), 10)]);
                        }
                    }
                }
            }
        }
        return cells;
    };
    /**
     * Retrieves the start and end time information of the specific cell element.
     *
     * @function getCellDetails
     * @param {Element | Element[]} tdCol Accepts the single or collection of elements.
     * @returns {CellClickEventArgs} Object An object holding the startTime, endTime and all-day information along with the target HTML element will be returned.
     */
    Schedule.prototype.getCellDetails = function (tdCol) {
        var td = (tdCol instanceof Array) ? tdCol : [tdCol];
        var firstTd = getElement(td[0]);
        var lastTd = getElement(td.slice(-1)[0]);
        var startTime = this.getDateFromElement(firstTd);
        var endTime = this.getDateFromElement(lastTd);
        if (isNullOrUndefined(startTime) || isNullOrUndefined(endTime)) {
            return undefined;
        }
        var endDateFromColSpan = this.activeView.isTimelineView() && !isNullOrUndefined(lastTd.getAttribute('colSpan')) &&
            this.headerRows.length > 0;
        var duration = endDateFromColSpan ? parseInt(lastTd.getAttribute('colSpan'), 10) : 1;
        if (!this.activeViewOptions.timeScale.enable || endDateFromColSpan || lastTd.classList.contains(cls.ALLDAY_CELLS_CLASS) ||
            lastTd.classList.contains(cls.HEADER_CELLS_CLASS)) {
            endTime = util.addDays(new Date(endTime.getTime()), duration);
        }
        else {
            endTime = this.activeView.getEndDateFromStartDate(endTime);
        }
        var data = {
            startTime: startTime,
            endTime: endTime,
            isAllDay: this.isAllDayCell(firstTd),
            element: tdCol
        };
        var groupIndex = firstTd.getAttribute('data-group-index');
        if (!isNullOrUndefined(groupIndex)) {
            data.groupIndex = parseInt(groupIndex, 10);
        }
        return data;
    };
    /**
     * Retrieves the selected cell elements.
     *
     * @function getSelectedElements
     * @returns {Element[]} The elements of currently selected cells will be returned.
     */
    Schedule.prototype.getSelectedElements = function () {
        if (this.keyboardInteractionModule && this.keyboardInteractionModule.selectedCells.length > 0) {
            return this.keyboardInteractionModule.selectedCells;
        }
        return this.getSelectedCells();
    };
    /**
     * To get the resource collection
     *
     * @function getResourceCollections
     * @returns {ResourcesModel[]} Returns the resource collections
     */
    Schedule.prototype.getResourceCollections = function () {
        return this.resourceCollection;
    };
    /**
     * To set the resource collection
     *
     * @function setResourceCollections
     * @param {ResourcesModel[]} resourceCol Accepts the resource collections in ResourcesModel type
     * @param {boolean} isEventDataRefresh Accepts the boolean to refresh the appointment data source from remote or local
     * @returns {void}
     */
    Schedule.prototype.setResourceCollections = function (resourceCol, isEventDataRefresh) {
        if (isEventDataRefresh === void 0) { isEventDataRefresh = true; }
        if (!isEventDataRefresh && this.uiStateValues) {
            this.uiStateValues.isPreventEventRefresh = true;
        }
        this.setProperties({ resources: resourceCol }, false);
    };
    /**
     * Current View could be change based on the provided parameters.
     *
     * @function changeCurrentView
     * @param {View} viewName Accept the view in the viewCollections.
     * @param {number} viewIndex Accept the viewIndex in the viewCollections.
     * @returns {void}
     */
    Schedule.prototype.changeCurrentView = function (viewName, viewIndex) {
        var index = this.getViewIndex(viewName);
        var view = viewName.charAt(0).toLowerCase() + viewName.slice(1);
        var viewOptions = this.viewOptions["" + view];
        if (viewOptions) {
            index = this.viewCollections.indexOf(viewOptions[viewIndex || 0]);
        }
        if (index === -1 || index === this.viewIndex) {
            return;
        }
        this.changeView(viewName, null, null, index);
    };
    /**
     * Return the current view Index.
     *
     * @function getCurrentViewIndex
     * @returns {number} Returns the view index
     */
    Schedule.prototype.getCurrentViewIndex = function () {
        return this.viewIndex;
    };
    /**
     * Retrieves the resource details based on the provided resource index.
     *
     * @param {number} index index of the resources at the last level.
     * @returns {ResourceDetails} Object An object holding the details of resource and resourceData.
     */
    Schedule.prototype.getResourcesByIndex = function (index) {
        if (this.resourceBase && this.resourceBase.lastResourceLevel) {
            if (index < 0 || index >= this.resourceBase.lastResourceLevel.length) {
                return undefined;
            }
            var data = this.resourceBase.lastResourceLevel[parseInt(index.toString(), 10)];
            var groupData = {};
            this.resourceBase.setResourceValues(groupData, index);
            return { resource: data.resource, resourceData: data.resourceData, groupData: groupData };
        }
        return undefined;
    };
    /**
     * This method allows to expand the resource that available on the scheduler.
     *
     * @function expandResource
     * @param {string | number} resourceId Accepts the resource id in either string or number type
     * @param {string} name Accepts the name of the resource collection
     * @returns {void}
     */
    Schedule.prototype.expandResource = function (resourceId, name) {
        if (this.activeView.isTimelineView() && this.resourceBase && this.resourceCollection.length > 1) {
            this.resourceBase.resourceExpand(resourceId, name, false);
        }
    };
    /**
     * This method allows to collapse the resource that available on the scheduler.
     *
     * @function collapseResource
     * @param {string | number} resourceId Accepts the resource id in either string or number type
     * @param {string} name Accepts the name of the resource collection
     * @returns {void}
     */
    Schedule.prototype.collapseResource = function (resourceId, name) {
        if (this.activeView.isTimelineView() && this.resourceBase && this.resourceCollection.length > 1) {
            this.resourceBase.resourceExpand(resourceId, name, true);
        }
    };
    /**
     * Scrolls the Schedule content area to the specified time.
     *
     * @function scrollTo
     * @param {string} hour Accepts the time value in the skeleton format of 'Hm'.
     * @param {Date} scrollDate Accepts the date object value.
     * @returns {void}
     */
    Schedule.prototype.scrollTo = function (hour, scrollDate) {
        if (this.currentView.indexOf('Agenda') < 0 && isNullOrUndefined(this.element.querySelector('.e-work-cells'))) {
            return;
        }
        if (this.activeView.scrollToDate && isNullOrUndefined(hour) && scrollDate) {
            this.activeView.scrollToDate(scrollDate);
        }
        else if (this.activeView.scrollToHour) {
            this.activeView.scrollToHour(hour, scrollDate);
        }
    };
    /**
     * This method allows scroll to the position of the any resources that available on the scheduler.
     * This method is applicable for without Agenda and Month agenda views of the schedule.
     *
     * @function scrollToResource
     * @param {string | number} resourceId Accepts the resource id in either string or number type
     * @param {string} groupName Accepts the name of the resource collection
     * @returns {void}
     */
    Schedule.prototype.scrollToResource = function (resourceId, groupName) {
        if (this.resourceBase && this.resourceBase.lastResourceLevel) {
            this.resourceBase.resourceScroll(resourceId, groupName);
        }
    };
    /**
     * Exports the Scheduler events to a calendar (.ics) file. By default, the calendar is exported with a file name `Calendar.ics`.
     * To change this file name on export, pass the custom string value as `fileName` to get the file downloaded with this provided name.
     *
     * @function exportToICalendar
     * @param {string} fileName Accepts the string value.
     * @param {Object[]} customData Accepts the collection of objects.
     * @returns {void}
     */
    Schedule.prototype.exportToICalendar = function (fileName, customData) {
        if (this.iCalendarExportModule) {
            this.iCalendarExportModule.initializeCalendarExport(fileName, customData);
        }
        else {
            console.warn('[WARNING] :: Module "ICalendarExport" is not available in Schedule component!' +
                ' You either misspelled the module name or forgot to load it.');
            throw Error('Inject ICalendarExport module');
        }
    };
    /**
     * Imports the events from an .ics file downloaded from any of the calendars like Google or Outlook into the Scheduler.
     * This method accepts the blob object or string format of an .ics file to be imported as a mandatory argument.
     *
     * @function importICalendar
     * @param {Blob | string} fileContent Accepts the file object or string format of an .ics file.
     * @returns {void}
     */
    Schedule.prototype.importICalendar = function (fileContent) {
        if (this.iCalendarImportModule) {
            this.iCalendarImportModule.initializeCalendarImport(fileContent);
        }
        else {
            console.warn('[WARNING] :: Module "ICalendarImport" is not available in Schedule component!' +
                ' You either misspelled the module name or forgot to load it.');
            throw Error('Inject ICalendarImport module');
        }
    };
    /**
     * Adds the newly created event into the Schedule dataSource.
     *
     * @function addEvent
     * @param {Object | Object[]} data Single or collection of event objects to be added into Schedule.
     * @returns {void}
     */
    Schedule.prototype.addEvent = function (data) {
        this.crudModule.addEvent(data);
    };
    /**
     * Generates the occurrences of a single recurrence event based on the provided event.
     *
     * @function generateEventOccurrences
     * @param {Object} event Accepts the parent recurrence event from which the occurrences are generated.
     * @param {Date} startDate Accepts the start date for the event occurrences. If not provided, the event's start date will be used.
     * @returns {Object[]} Returns the collection of occurrence event objects.
     */
    Schedule.prototype.generateEventOccurrences = function (event, startDate) {
        return (this.eventBase) ? this.eventBase.generateOccurrence(event, startDate) : [];
    };
    /**
     * Allows the Scheduler events data to be exported as an Excel file either in .xlsx or .csv file formats.
     * By default, the whole event collection bound to the Scheduler gets exported as an Excel file.
     * To export only the specific events of Scheduler, you need to pass the custom data collection as
     * a parameter to this `exportToExcel` method. This method accepts the export options as arguments such as fileName,
     * exportType, fields, customData, and includeOccurrences. The `fileName` denotes the name to be given for the exported
     * file and the `exportType` allows you to set the format of an Excel file to be exported either as .xlsx or .csv.
     * The custom or specific field collection of event dataSource to be exported can be provided through `fields` option
     * and the custom data collection can be exported by passing them through the `customData` option. There also exists
     * option to export each individual instances of the recurring events to an Excel file, by setting true or false to the
     * `includeOccurrences` option, denoting either to include or exclude the occurrences as separate instances on an exported Excel file.
     *
     * @function exportToExcel
     * @param {ExportOptions} excelExportOptions The export options to be set before start with exporting the Scheduler events to an Excel file.
     * @returns {void}
     */
    Schedule.prototype.exportToExcel = function (excelExportOptions) {
        if (this.excelExportModule) {
            this.excelExportModule.initializeExcelExport(excelExportOptions);
        }
        else {
            console.warn('[WARNING] :: Module "ExcelExport" is not available in Schedule component!' +
                ' You either misspelled the module name or forgot to load it.');
            throw Error('Inject ExcelExport module');
        }
    };
    /**
     * Method allows to print the scheduler.
     *
     * @function print
     * @param {ScheduleModel} printOptions The export options to be set before start with exporting
     * the Scheduler events to the print window.
     * @returns {void}
     */
    Schedule.prototype.print = function (printOptions) {
        if (this.printModule) {
            this.printModule.print(printOptions);
        }
        else {
            console.warn('[WARNING] :: Module "Print" is not available in Schedule component!' +
                ' You either misspelled the module name or forgot to load it.');
            throw Error('Inject Print module');
        }
    };
    /**
     * Updates the changes made in the event object by passing it as an parameter into the dataSource.
     *
     * @function saveEvent
     * @param {Object | Object[]} data Single or collection of event objects to be saved into Schedule.
     * @param {CurrentAction} currentAction Denotes the action that takes place either for editing occurrence or series.
     *  The valid current action names are `EditOccurrence` or `EditSeries`.
     * @returns {void}
     */
    Schedule.prototype.saveEvent = function (data, currentAction) {
        this.crudModule.saveEvent(data, currentAction);
    };
    /**
     * Deletes the events based on the provided ID or event collection in the argument list.
     *
     * @function deleteEvent
     * @param {string | number | Object | Object[]} id Accepts the ID as string or number type or single or collection of the event object which needs to be removed from the Schedule.
     * @param {CurrentAction} currentAction Denotes the delete action that takes place either on occurrence or series events.
     *  The valid current action names are `Delete`, `DeleteOccurrence` or `DeleteSeries`.
     * @returns {void}
     */
    Schedule.prototype.deleteEvent = function (id, currentAction) {
        this.crudModule.deleteEvent(id, currentAction);
    };
    /**
     * Retrieves the entire collection of events bound to the Schedule.
     *
     * @function getEvents
     * @param {Date} startDate Accepts the start date.
     * @param {Date} endDate Accepts te end date.
     * @param {boolean} includeOccurrences Accepts the boolean value to process the occurrence from recurrence series.
     * @returns {Object[]} Returns the collection of event objects from the Schedule.
     */
    Schedule.prototype.getEvents = function (startDate, endDate, includeOccurrences) {
        var eventCollections = [];
        if (includeOccurrences) {
            eventCollections = this.eventBase.getProcessedEvents();
        }
        else {
            eventCollections = this.eventsData;
        }
        if (startDate) {
            startDate = this.getDateTime(startDate);
        }
        if (endDate) {
            endDate = this.getDateTime(endDate);
        }
        eventCollections = this.eventBase.filterEventsByRange(eventCollections, startDate, endDate);
        return eventCollections;
    };
    /**
     * Retrieves the entire collection of block events bound to the Schedule.
     *
     * @function getBlockEvents
     * @param {Date} startDate Accepts the start date.
     * @param {Date} endDate Accepts te end date.
     * @param {boolean} includeOccurrences Accepts the boolean value to process the occurrence from recurrence series.
     * @returns {Object[]} Returns the collection of block event objects from the Schedule.
     */
    Schedule.prototype.getBlockEvents = function (startDate, endDate, includeOccurrences) {
        var eventCollections = [];
        if (includeOccurrences) {
            eventCollections = this.eventBase.getProcessedEvents(this.blockData);
        }
        else {
            eventCollections = this.blockData;
        }
        if (startDate) {
            startDate = this.getDateTime(startDate);
        }
        if (endDate) {
            endDate = this.getDateTime(endDate);
        }
        eventCollections = this.eventBase.filterEventsByRange(eventCollections, startDate, endDate);
        return eventCollections;
    };
    /**
     * Retrieves the occurrences of a single recurrence event based on the provided parent ID.
     *
     * @function getOccurrencesByID
     * @param {number} eventID ID of the parent recurrence data from which the occurrences are fetched.
     * @returns {Object[]} Returns the collection of occurrence event objects.
     */
    Schedule.prototype.getOccurrencesByID = function (eventID) {
        return this.eventBase.getOccurrencesByID(eventID);
    };
    /**
     * Retrieves all the occurrences that lies between the specific start and end time range.
     *
     * @function getOccurrencesByRange
     * @param {Date} startTime Denotes the start time range.
     * @param {Date} endTime Denotes the end time range.
     * @returns {Object[]} Returns the collection of occurrence event objects that lies between the provided start and end time.
     */
    Schedule.prototype.getOccurrencesByRange = function (startTime, endTime) {
        startTime = this.getDateTime(startTime);
        endTime = this.getDateTime(endTime);
        return this.eventBase.getOccurrencesByRange(startTime, endTime);
    };
    /**
     * Retrieves the dates that lies on active view of Schedule.
     *
     * @function getCurrentViewDates
     * @returns {Date[]} Returns the collection of dates.
     */
    Schedule.prototype.getCurrentViewDates = function () {
        return this.activeView ? this.activeView.renderDates : [];
    };
    /**
     * Set the recurrence editor instance from custom editor template.
     *
     * @function setRecurrenceEditor
     * @param {RecurrenceEditor} recurrenceEditor instance has passed to fetch the instance in event window.
     * @returns {void}
     */
    Schedule.prototype.setRecurrenceEditor = function (recurrenceEditor) {
        this.eventWindow.setRecurrenceEditor(recurrenceEditor);
    };
    /**
     * Get the maximum id of an event.
     *
     * @function getEventMaxID
     * @returns {number | string} Returns the maximum ID from scheduler data collections.
     */
    Schedule.prototype.getEventMaxID = function () {
        return this.eventBase.getEventMaxID();
    };
    /**
     * Get deleted occurrences from given recurrence series.
     *
     * @function getDeletedOccurrences
     * @param {string | number | Object} recurrenceData Accepts the parent ID of the event object or parent event object
     * @returns {Object[]} Returns the collection of deleted occurrence events.
     */
    Schedule.prototype.getDeletedOccurrences = function (recurrenceData) {
        return this.eventBase.getDeletedOccurrences(recurrenceData);
    };
    /**
     * Retrieves the events that lies on the current date range of the active view of Schedule.
     *
     * @function getCurrentViewEvents
     * @returns {Object[]} Returns the collection of events.
     */
    Schedule.prototype.getCurrentViewEvents = function () {
        return this.eventsProcessed;
    };
    /**
     * Refreshes the event dataSource. This method may be useful when the events alone in the schedule needs to be re-rendered.
     *
     * @function refreshEvents
     * @param {boolean} isRemoteRefresh Accepts the boolean to refresh data from remote or local
     * @returns {void}
     */
    Schedule.prototype.refreshEvents = function (isRemoteRefresh) {
        if (isRemoteRefresh === void 0) { isRemoteRefresh = true; }
        if (isRemoteRefresh) {
            if (this.dragAndDropModule) {
                this.dragAndDropModule.actionObj.action = '';
                removeClass([this.element], cls.EVENT_ACTION_CLASS);
            }
            this.crudModule.refreshDataManager();
        }
        else {
            if (this.uiStateValues) {
                this.uiStateValues.isPreventTimezone = true;
            }
            if (this.crudModule) {
                this.crudModule.refreshProcessedData();
            }
            if (this.uiStateValues) {
                this.uiStateValues.isPreventTimezone = false;
            }
        }
    };
    /**
     * Method to refresh the given Schedule templates
     *
     * @param {string} templateName Accepts the template name
     * @returns {void}
     */
    Schedule.prototype.refreshTemplates = function (templateName) {
        if (templateName) {
            this.resetTemplates([templateName]);
        }
        else {
            this.resetTemplates();
        }
        switch (templateName) {
            case 'eventTemplate':
                this.appointmentTemplateFn = this.templateParser(this.activeViewOptions.eventTemplate);
                this.refreshEvents(false);
                break;
            case 'dateHeaderTemplate':
                this.dateHeaderTemplateFn = this.templateParser(this.activeViewOptions.dateHeaderTemplate);
                this.activeView.refreshHeader();
                break;
            case 'dateRangeTemplate':
                this.dateRangeTemplateFn = this.templateParser(this.activeViewOptions.dateRangeTemplate);
                if (this.headerModule) {
                    this.headerModule.refresh();
                }
                break;
            case 'resourceHeaderTemplate':
                this.resourceHeaderTemplateFn = this.templateParser(this.activeViewOptions.resourceHeaderTemplate);
                if (this.activeView.isTimelineView()) {
                    this.activeView.refreshResourceHeader();
                }
                else {
                    this.activeView.refreshHeader();
                }
                break;
            case 'quickInfoTemplates':
                if (this.quickPopup) {
                    this.quickPopup.destroy();
                    this.quickPopup = null;
                }
                this.quickPopup = new QuickPopups(this);
                this.quickInfoTemplatesHeaderFn = this.templateParser(this.quickInfoTemplates.header);
                this.quickInfoTemplatesContentFn = this.templateParser(this.quickInfoTemplates.content);
                this.quickInfoTemplatesFooterFn = this.templateParser(this.quickInfoTemplates.footer);
                break;
            case 'editorTemplate':
                this.destroyEditorWindow();
                this.editorTemplateFn = this.templateParser(this.editorTemplate);
                break;
            case 'editorHeaderTemplate':
                this.destroyEditorWindow();
                this.editorHeaderTemplateFn = this.templateParser(this.editorHeaderTemplate);
                break;
            case 'editorFooterTemplate':
                this.destroyEditorWindow();
                this.editorFooterTemplateFn = this.templateParser(this.editorFooterTemplate);
                break;
            case 'tooltipTemplate':
            case 'headerTooltipTemplate':
                if (this.eventTooltip) {
                    this.eventTooltip.destroy();
                    this.eventTooltip = null;
                }
                this.eventTooltip = new EventTooltip(this);
                this.eventTooltipTemplateFn = this.templateParser(this.eventSettings.tooltipTemplate);
                this.headerTooltipTemplateFn = this.templateParser(this.activeViewOptions.group.headerTooltipTemplate);
                break;
            default:
                this.initializeView(this.currentView);
                break;
        }
    };
    /**
     * Refreshes the Schedule layout without re-render.
     *
     * @function refreshLayout
     * @returns {void}
     */
    Schedule.prototype.refreshLayout = function () {
        this.onScheduleResize();
        if (this.headerModule) {
            this.headerModule.refresh();
        }
        if (this.eventWindow) {
            this.eventWindow.refresh();
        }
    };
    /**
     * To retrieve the appointment object from element.
     *
     * @function getEventDetails
     * @param {Element} element Denotes the event UI element on the Schedule.
     * @returns {Object} Returns the event details.
     */
    Schedule.prototype.getEventDetails = function (element) {
        element = getElement(element);
        var guid = element.getAttribute('data-guid');
        if (guid) {
            return this.eventBase.getEventByGuid(guid);
        }
        return {};
    };
    /**
     * To check whether the given time range slots are available for event creation or already occupied by other events.
     * This method currently focuses on validating appointments within the current view date range.
     * However, it does not extend this availability check to recurrence occurrences outside of the current date range.
     *
     * @function isSlotAvailable
     * @param {Date | Object} startTime Denotes the start time of the slot.
     * @param {Date} endTime Denotes the end time of the slot.
     * @param {number} groupIndex Defines the resource index from last level.
     * @returns {boolean} Returns true, if the slot that lies in the provided time range does not contain any other events.
     */
    Schedule.prototype.isSlotAvailable = function (startTime, endTime, groupIndex) {
        var _this = this;
        var eventStart;
        var eventEnd;
        var eventObj = this.activeEventData.event;
        if (startTime instanceof Date || typeof (startTime) === 'string') {
            eventStart = startTime;
            eventEnd = endTime;
        }
        else {
            eventObj = startTime;
            eventStart = startTime[this.eventFields.startTime];
            eventEnd = startTime[this.eventFields.endTime];
            if (this.resourceBase) {
                groupIndex = this.eventBase.getGroupIndexFromEvent(startTime);
            }
        }
        if (isNullOrUndefined(eventStart) || isNullOrUndefined(eventEnd)) {
            return true;
        }
        eventStart = this.getDateTime(eventStart);
        eventEnd = this.getDateTime(eventEnd);
        var eventCollection = this.eventBase.filterEvents(eventStart, eventEnd);
        if (!isNullOrUndefined(groupIndex) && this.resourceBase && this.resourceBase.lastResourceLevel.length > 0) {
            eventCollection =
                this.eventBase.filterEventsByResource(this.resourceBase.lastResourceLevel[parseInt(groupIndex.toString(), 10)], eventCollection);
        }
        if (eventObj) {
            if (eventObj.Guid) {
                eventCollection = eventCollection.filter(function (event) { return event.Guid !== eventObj.Guid; });
            }
            else {
                eventCollection = eventCollection.filter(function (event) {
                    return event[_this.eventFields.id] !== eventObj[_this.eventFields.id];
                });
            }
        }
        return (eventCollection.length > 0) ? false : true;
    };
    /**
     * To manually open the event editor on specific time or on certain events.
     *
     * @function openEditor
     * @param {Object} data It can be either cell data or event data.
     * @param {CurrentAction} action Defines the action for which the editor needs to be opened such as either for new event creation or
     *  for editing of existing events. The applicable action names that can be used here are `Add`, `Save`, `EditOccurrence`
     *  and `EditSeries`.
     * @param {boolean} isEventData It allows to decide whether the editor needs to be opened with the clicked cell details or with the
     *  passed event details.
     * @param {number} repeatType It opens the editor with the recurrence options based on the provided repeat type.
     * @returns {void}
     */
    Schedule.prototype.openEditor = function (data, action, isEventData, repeatType) {
        if (action === 'Add' && !isEventData) {
            data.startTime = this.getDateTime(data.startTime);
            data.endTime = this.getDateTime(data.endTime);
            if (!isNullOrUndefined(data.element)) {
                data.element = getElement(data.element);
            }
        }
        else {
            data[this.eventFields.startTime] = this.getDateTime(data[this.eventFields.startTime]);
            data[this.eventFields.endTime] = this.getDateTime(data[this.eventFields.endTime]);
        }
        this.currentAction = action;
        if (action !== 'Add') {
            this.activeEventData.event = data;
        }
        this.eventWindow.openEditor(data, action, isEventData, repeatType);
    };
    /**
     * To manually close the event editor window
     *
     * @function closeEditor
     * @returns {void}
     */
    Schedule.prototype.closeEditor = function () {
        if (this.eventWindow) {
            this.eventWindow.dialogClose();
        }
    };
    /**
     * To manually open the quick info popup based on cell or event details.
     *
     * @param {object} data Defines the cell or event data. If the data contains valid ID, it will open event quick info popup,
     * otherwise cell quick info popup displayed.
     * @returns {void}
     */
    Schedule.prototype.openQuickInfoPopup = function (data) {
        var _this = this;
        if (this.currentView === 'Year' || isNullOrUndefined(data)) {
            return;
        }
        if (isNullOrUndefined(data[this.eventFields.id])) {
            if (this.currentView === 'Agenda' || this.currentView === 'MonthAgenda' || isNullOrUndefined(this.activeView)) {
                return;
            }
            var cellData = {
                startTime: this.activeCellsData.startTime = this.getDateTime(data[this.eventFields.startTime]),
                endTime: this.activeCellsData.endTime = this.getDateTime(data[this.eventFields.endTime]),
                isAllDay: this.activeCellsData.isAllDay =
                    !isNullOrUndefined(data[this.eventFields.isAllDay]) ? data[this.eventFields.isAllDay] : false
            };
            var startTime = this.activeView.getAdjustedDate(new Date(cellData.startTime));
            if (startTime) {
                var query = '.' + cls.WORK_CELLS_CLASS + '[data-date="' + startTime.getTime() + '"]';
                if (this.activeViewOptions.group.resources.length > 0 && !this.uiStateValues.isGroupAdaptive
                    && this.resourceBase && this.eventBase) {
                    cellData.groupIndex = this.eventBase.getGroupIndexFromEvent(data);
                    query = '.' + cls.WORK_CELLS_CLASS + '[data-date="' + startTime.getTime() + '"][data-group-index="' + cellData.groupIndex + '"]';
                }
                var workCell = this.element.querySelector(query);
                if (workCell) {
                    workCell.focus();
                    cellData.element = workCell;
                    this.notify(events.cellClick, cellData);
                }
            }
        }
        else {
            var app = this.getCurrentViewEvents().filter(function (item) {
                return data[_this.eventFields.id] === item[_this.eventFields.id];
            });
            if (app.length <= 0) {
                return;
            }
            var selectEvent = app[0];
            if (data[this.eventFields.recurrenceRule]) {
                var occurence = app.filter(function (x) {
                    return x[_this.eventFields.startTime].getTime() === data[_this.eventFields.startTime].getTime();
                });
                if (occurence.length > 0) {
                    selectEvent = occurence[0];
                }
            }
            var element = this.element.querySelector('div[data-guid="' + selectEvent.Guid + '"]');
            if (element) {
                this.eventBase.removeSelectedAppointmentClass();
                this.eventBase.addSelectedAppointments([element], false);
                this.activeEventData = { event: selectEvent, element: element };
                if (this.currentView === 'Agenda' || this.currentView === 'MonthAgenda') {
                    addClass([this.activeEventData.element], cls.AGENDA_SELECTED_CELL);
                }
                this.notify(events.eventClick, this.activeEventData);
            }
        }
    };
    /**
     * To manually close the quick info popup
     *
     * @function closeQuickInfoPopup
     * @returns {void}
     */
    Schedule.prototype.closeQuickInfoPopup = function () {
        if (this.quickPopup) {
            this.quickPopup.quickPopupHide(true);
        }
    };
    /**
     * Closes the tooltip.
     * For example, when the context menu is opened for an event,
     * the tooltip can be closed by calling this method.
     *
     * @function closeTooltip
     * @returns {void}
     */
    Schedule.prototype.closeTooltip = function () {
        if (this.eventTooltip) {
            this.eventTooltip.close();
        }
    };
    /**
     * Select the resource based on group index in mobile mode.
     *
     * @param {number} groupIndex Defines the resource index based on last level.
     * @returns {void}
     */
    Schedule.prototype.selectResourceByIndex = function (groupIndex) {
        if (this.resourceBase && this.uiStateValues.isGroupAdaptive) {
            this.resourceBase.selectResourceByIndex(groupIndex);
        }
    };
    /**
     * Select the resources to the based on id.
     *
     * @param {string | number} id id of the resource defined in resources collection.
     * @param {string} name Name of the resource defined in resources collection.
     * @returns {number} Returns the group index
     */
    Schedule.prototype.getIndexFromResourceId = function (id, name) {
        if (this.resourceBase) {
            return this.resourceBase.getIndexFromResourceId(id, name);
        }
        return null;
    };
    /**
     * Adds the resources to the specified index.
     *
     * @param {Object | Object[]} resources Accepts the resource data in single or collection of data.
     * @param {string} name Name of the resource defined in resources collection.
     * @param {number} index Index or position where the resource should be added.
     * @returns {void}
     */
    Schedule.prototype.addResource = function (resources, name, index) {
        this.resourceBase.addResource(resources, name, index);
    };
    /**
     * Removes the specified resource.
     *
     * @param {string | string[] | number | number[]} resourceId Specifies the resource id to be removed.
     * @param {string} name Specifies the resource name from which the id should be referred.
     * @returns {void}
     */
    Schedule.prototype.removeResource = function (resourceId, name) {
        this.resourceBase.removeResource(resourceId, name);
    };
    /**
     * Destroys the Schedule component.
     *
     * @function destroy
     * @returns {void}
     */
    Schedule.prototype.destroy = function () {
        if (this.eventTooltip) {
            this.eventTooltip.destroy();
            this.eventTooltip = null;
        }
        this.destroyPopups();
        this.hideSpinner();
        this.unWireEvents();
        this.destroyHeaderModule();
        if (this.eventBase) {
            this.eventBase.destroy();
            this.eventBase = null;
        }
        if (this.workCellAction) {
            this.workCellAction.destroy();
            this.workCellAction = null;
        }
        if (this.inlineModule) {
            this.inlineModule.destroy();
            this.inlineModule = null;
        }
        if (this.keyboardInteractionModule) {
            this.keyboardInteractionModule.destroy();
            this.keyboardInteractionModule = null;
        }
        if (this.scrollModule) {
            this.scrollModule.destroy();
            this.scrollModule = null;
        }
        if (this.printModule) {
            this.printModule.destroy();
        }
        if (this.activeView) {
            this.resetTemplates();
            this.activeView.removeEventListener();
            this.activeView.destroy();
            this.activeView = null;
        }
        if (this.scheduleTouchModule) {
            this.scheduleTouchModule.destroy();
            this.scheduleTouchModule = null;
        }
        if (this.crudModule) {
            this.crudModule.destroy();
            this.crudModule = null;
        }
        if (this.dataModule) {
            this.dataModule.destroy();
            this.dataModule = null;
        }
        _super.prototype.destroy.call(this);
        var modules = [
            'dayModule', 'weekModule', 'workWeekModule', 'monthModule', 'monthAgendaModule', 'yearModule', 'agendaModule',
            'timelineViewsModule', 'timelineMonthModule', 'timelineYearModule', 'resizeModule', 'dragAndDropModule',
            'excelExportModule', 'printModule', 'iCalendarExportModule', 'iCalendarImportModule', 'tzModule', 'eventsData',
            'eventsProcessed', 'blockData', 'blockProcessed', 'uiStateValues', 'viewCollections', 'viewOptions', 'defaultLocale',
            'localeObj', 'selectedElements', 'resourceCollection', 'editorTitles', 'eventFields', 'activeViewOptions',
            'activeEventData', 'activeCellsData', 'renderModule'
        ];
        for (var _i = 0, modules_1 = modules; _i < modules_1.length; _i++) {
            var module = modules_1[_i];
            this["" + module] = null;
        }
        util.removeChildren(this.element);
        var removeClasses = [cls.ROOT, cls.RTL, cls.DEVICE_CLASS, cls.MULTI_DRAG];
        if (this.cssClass) {
            removeClasses = removeClasses.concat(this.cssClass.split(' '));
        }
        removeClass([this.element], removeClasses);
    };
    __decorate([
        Property('auto')
    ], Schedule.prototype, "width", void 0);
    __decorate([
        Property('auto')
    ], Schedule.prototype, "height", void 0);
    __decorate([
        Property(true)
    ], Schedule.prototype, "showHeaderBar", void 0);
    __decorate([
        Property(true)
    ], Schedule.prototype, "showTimeIndicator", void 0);
    __decorate([
        Property(true)
    ], Schedule.prototype, "allowSwiping", void 0);
    __decorate([
        Collection([], ToolbarItem)
    ], Schedule.prototype, "toolbarItems", void 0);
    __decorate([
        Property('Week')
    ], Schedule.prototype, "currentView", void 0);
    __decorate([
        Property(['Day', 'Week', 'WorkWeek', 'Month', 'Agenda'])
    ], Schedule.prototype, "views", void 0);
    __decorate([
        Property(new Date())
    ], Schedule.prototype, "selectedDate", void 0);
    __decorate([
        Property(new Date(1900, 0, 1))
    ], Schedule.prototype, "minDate", void 0);
    __decorate([
        Property(new Date(2099, 11, 31))
    ], Schedule.prototype, "maxDate", void 0);
    __decorate([
        Property()
    ], Schedule.prototype, "dateFormat", void 0);
    __decorate([
        Property('Gregorian')
    ], Schedule.prototype, "calendarMode", void 0);
    __decorate([
        Property(true)
    ], Schedule.prototype, "showWeekend", void 0);
    __decorate([
        Property(0)
    ], Schedule.prototype, "firstDayOfWeek", void 0);
    __decorate([
        Property('FirstDay')
    ], Schedule.prototype, "weekRule", void 0);
    __decorate([
        Property([1, 2, 3, 4, 5])
    ], Schedule.prototype, "workDays", void 0);
    __decorate([
        Property(12)
    ], Schedule.prototype, "monthsCount", void 0);
    __decorate([
        Property('00:00')
    ], Schedule.prototype, "startHour", void 0);
    __decorate([
        Property('24:00')
    ], Schedule.prototype, "endHour", void 0);
    __decorate([
        Property(null)
    ], Schedule.prototype, "timeFormat", void 0);
    __decorate([
        Property(true)
    ], Schedule.prototype, "enableHtmlSanitizer", void 0);
    __decorate([
        Property(false)
    ], Schedule.prototype, "enableAllDayScroll", void 0);
    __decorate([
        Property(false)
    ], Schedule.prototype, "enableAdaptiveUI", void 0);
    __decorate([
        Property(true)
    ], Schedule.prototype, "allowResizing", void 0);
    __decorate([
        Complex({}, WorkHours)
    ], Schedule.prototype, "workHours", void 0);
    __decorate([
        Complex({}, TimeScale)
    ], Schedule.prototype, "timeScale", void 0);
    __decorate([
        Property(true)
    ], Schedule.prototype, "allowKeyboardInteraction", void 0);
    __decorate([
        Property(true)
    ], Schedule.prototype, "allowDragAndDrop", void 0);
    __decorate([
        Property()
    ], Schedule.prototype, "dateHeaderTemplate", void 0);
    __decorate([
        Property()
    ], Schedule.prototype, "dateRangeTemplate", void 0);
    __decorate([
        Property()
    ], Schedule.prototype, "cellHeaderTemplate", void 0);
    __decorate([
        Property()
    ], Schedule.prototype, "dayHeaderTemplate", void 0);
    __decorate([
        Property()
    ], Schedule.prototype, "monthHeaderTemplate", void 0);
    __decorate([
        Property()
    ], Schedule.prototype, "cellTemplate", void 0);
    __decorate([
        Property(false)
    ], Schedule.prototype, "readonly", void 0);
    __decorate([
        Property(true)
    ], Schedule.prototype, "showQuickInfo", void 0);
    __decorate([
        Property(false)
    ], Schedule.prototype, "allowInline", void 0);
    __decorate([
        Property(true)
    ], Schedule.prototype, "allowMultiCellSelection", void 0);
    __decorate([
        Property(true)
    ], Schedule.prototype, "allowMultiRowSelection", void 0);
    __decorate([
        Property(false)
    ], Schedule.prototype, "quickInfoOnSelectionEnd", void 0);
    __decorate([
        Property(false)
    ], Schedule.prototype, "showWeekNumber", void 0);
    __decorate([
        Property(false)
    ], Schedule.prototype, "rowAutoHeight", void 0);
    __decorate([
        Property(false)
    ], Schedule.prototype, "allowMultiDrag", void 0);
    __decorate([
        Property(0)
    ], Schedule.prototype, "firstMonthOfYear", void 0);
    __decorate([
        Property()
    ], Schedule.prototype, "editorTemplate", void 0);
    __decorate([
        Property()
    ], Schedule.prototype, "editorHeaderTemplate", void 0);
    __decorate([
        Property()
    ], Schedule.prototype, "editorFooterTemplate", void 0);
    __decorate([
        Complex({}, QuickInfoTemplates)
    ], Schedule.prototype, "quickInfoTemplates", void 0);
    __decorate([
        Property(7)
    ], Schedule.prototype, "agendaDaysCount", void 0);
    __decorate([
        Property(true)
    ], Schedule.prototype, "hideEmptyAgendaDays", void 0);
    __decorate([
        Property(true)
    ], Schedule.prototype, "enableRecurrenceValidation", void 0);
    __decorate([
        Property()
    ], Schedule.prototype, "timezone", void 0);
    __decorate([
        Complex({}, EventSettings)
    ], Schedule.prototype, "eventSettings", void 0);
    __decorate([
        Property(timezoneData)
    ], Schedule.prototype, "timezoneDataSource", void 0);
    __decorate([
        Property()
    ], Schedule.prototype, "resourceHeaderTemplate", void 0);
    __decorate([
        Property()
    ], Schedule.prototype, "headerIndentTemplate", void 0);
    __decorate([
        Complex({}, Group)
    ], Schedule.prototype, "group", void 0);
    __decorate([
        Collection([], Resources)
    ], Schedule.prototype, "resources", void 0);
    __decorate([
        Collection([], HeaderRows)
    ], Schedule.prototype, "headerRows", void 0);
    __decorate([
        Property()
    ], Schedule.prototype, "cssClass", void 0);
    __decorate([
        Property()
    ], Schedule.prototype, "eventDragArea", void 0);
    __decorate([
        Event()
    ], Schedule.prototype, "created", void 0);
    __decorate([
        Event()
    ], Schedule.prototype, "destroyed", void 0);
    __decorate([
        Event()
    ], Schedule.prototype, "cellClick", void 0);
    __decorate([
        Event()
    ], Schedule.prototype, "cellDoubleClick", void 0);
    __decorate([
        Event()
    ], Schedule.prototype, "moreEventsClick", void 0);
    __decorate([
        Event()
    ], Schedule.prototype, "hover", void 0);
    __decorate([
        Event()
    ], Schedule.prototype, "select", void 0);
    __decorate([
        Event()
    ], Schedule.prototype, "actionBegin", void 0);
    __decorate([
        Event()
    ], Schedule.prototype, "actionComplete", void 0);
    __decorate([
        Event()
    ], Schedule.prototype, "actionFailure", void 0);
    __decorate([
        Event()
    ], Schedule.prototype, "navigating", void 0);
    __decorate([
        Event()
    ], Schedule.prototype, "renderCell", void 0);
    __decorate([
        Event()
    ], Schedule.prototype, "eventClick", void 0);
    __decorate([
        Event()
    ], Schedule.prototype, "eventDoubleClick", void 0);
    __decorate([
        Event()
    ], Schedule.prototype, "eventRendered", void 0);
    __decorate([
        Event()
    ], Schedule.prototype, "dataBinding", void 0);
    __decorate([
        Event()
    ], Schedule.prototype, "popupOpen", void 0);
    __decorate([
        Event()
    ], Schedule.prototype, "popupClose", void 0);
    __decorate([
        Event()
    ], Schedule.prototype, "dragStart", void 0);
    __decorate([
        Event()
    ], Schedule.prototype, "drag", void 0);
    __decorate([
        Event()
    ], Schedule.prototype, "dragStop", void 0);
    __decorate([
        Event()
    ], Schedule.prototype, "resizeStart", void 0);
    __decorate([
        Event()
    ], Schedule.prototype, "resizing", void 0);
    __decorate([
        Event()
    ], Schedule.prototype, "resizeStop", void 0);
    __decorate([
        Event()
    ], Schedule.prototype, "virtualScrollStart", void 0);
    __decorate([
        Event()
    ], Schedule.prototype, "virtualScrollStop", void 0);
    __decorate([
        Event()
    ], Schedule.prototype, "dataBound", void 0);
    Schedule = __decorate([
        NotifyPropertyChanges
    ], Schedule);
    return Schedule;
}(Component));
export { Schedule };
